/**
 * sqlBuilder.ts — TransformState types and pure SQL generation for TransformBar.
 *
 * Design principles:
 * - All functions are pure: state in → state/SQL out
 * - All generated SQL uses DuckDB syntax with double-quoted identifiers
 * - Template-based composition (no AST parsing needed)
 * - One-way flow: TransformBar → TransformState → SQL → Editor → Run
 */

import type { WorkspaceColumnProfile } from '../../services/workspaceApi';

// ── Identifier Quoting ───────────────────────────────────────────────

/** Double-quote a DuckDB identifier, escaping embedded double quotes. */
export function quoteIdent(name: string): string {
  return `"${name.replace(/"/g, '""')}"`;
}

// ── Types ────────────────────────────────────────────────────────────

export type FilterOperator =
  | '=' | '!=' | '>' | '<' | '>=' | '<='
  | 'LIKE' | 'NOT LIKE' | 'IN' | 'IS NULL' | 'IS NOT NULL';

export interface FilterClause {
  id: string;
  column: string;
  operator: FilterOperator;
  value: string; // unused for IS NULL / IS NOT NULL
  negate?: boolean; // wrap condition in NOT(...)
  logicOp?: 'AND' | 'OR'; // connector to the PREVIOUS filter; ignored on the first filter
}

export type SortDir = 'ASC' | 'DESC';

export interface OrderByClause {
  column: string;
  direction: SortDir;
}

export type AggregateFunc = 'COUNT' | 'SUM' | 'AVG' | 'MIN' | 'MAX' | 'COUNT_DISTINCT';

export interface AggregationClause {
  column: string;
  func: AggregateFunc;
  alias: string;
}

export type JoinType = 'INNER' | 'LEFT' | 'RIGHT' | 'FULL OUTER';

export interface JoinCondition {
  leftColumn: string;
  rightColumn: string;
}

export interface JoinClause {
  id: string;
  table: string;
  joinType: JoinType;
  conditions: JoinCondition[]; // at least one; generates: ON c1 AND c2 AND ...
  columns?: string[];
  /** Maps join source column name → output alias in the result set. */
  columnAliases?: Record<string, string>;
}

/** Vertical stack mode — UNION ALL BY NAME is the default (faster, preserves rows). */
export type UnionStackMode = 'ALL_BY_NAME' | 'BY_NAME';

/** How union branches align columns — auto uses BY NAME; by_position is advanced. */
export type UnionAlignMode = 'auto' | 'by_position';

export interface UnionClause {
  id: string;
  /** Workspace table to stack beneath the current transformed base query. */
  table: string;
  /** Empty means SELECT * — DuckDB aligns columns by name across branches. */
  columns?: string[];
  /** Schema snapshot captured at apply time — enables position align without extra API calls. */
  schemaSnapshot?: string[];
}

export interface LookupClause {
  id: string;
  refTable: string;
  sourceKey: string;
  refKey: string;
  columns: string[];
  keepUnmatched: boolean;
  matchBehavior: 'first' | 'all';
  /** If true, skip DISTINCT ON wrapper (Simple Join optimization) */
  isUnique?: boolean;
  /** Maps lookup source column name → output alias in the result set. */
  columnAliases?: Record<string, string>;
}

export type HavingOperator = '>' | '<' | '>=' | '<=' | '=' | '!=';

export interface HavingClause {
  id: string;
  /** 'COUNT_STAR' generates COUNT(*); all others generate FUNC("column"). */
  aggregateFunc: AggregateFunc | 'COUNT_STAR';
  column: string; // ignored when aggregateFunc === 'COUNT_STAR'
  operator: HavingOperator;
  value: string;
}

export type ReplaceMissingStrategy = 'constant' | 'forward_fill' | 'mean' | 'median' | 'mode';

export interface CaseWhenClause {
  column: string;
  operator: FilterOperator;
  value: string; // unused for IS NULL / IS NOT NULL
  result: string;
}

export interface SelectColumn {
  expression: string; // e.g. 'UPPER("email")'
  alias: string; // e.g. 'email'
  /** Resolved output SQL type when known (e.g. from applyCast). */
  outputType?: string;
  /** When true, this column replaces an existing column's VALUE in * via DuckDB REPLACE clause.
   *  The alias must match an existing column name. E.g. REPLACE (UPPER("city") AS "city") */
  isReplacement?: boolean;
  /** When true, this column renames an existing column via DuckDB RENAME clause.
   *  Expression is the old column name, alias is the new name. E.g. RENAME ("city" AS "city_name") */
  isRename?: boolean;
}

export type WindowFunctionName =
  | 'ROW_NUMBER'
  | 'RANK'
  | 'DENSE_RANK'
  | 'NTILE'
  | 'RUNNING_SUM'
  | 'RUNNING_COUNT'
  | 'SUM'
  | 'AVG'
  | 'COUNT'
  | 'MIN'
  | 'MAX'
  | 'LAG'
  | 'LEAD'
  | 'FIRST_VALUE'
  | 'LAST_VALUE';

export interface WindowFunctionClause {
  outputCol: string;
  func: WindowFunctionName;
  valueCol?: string;
  ntileBuckets?: number;
  partitionBy: string[];
  orderBy?: { col: string; dir: SortDir };
  frame?: string;
}

export interface TransformState {
  baseTable: string;
  selectColumns: SelectColumn[]; // empty means SELECT *
  excludeColumns: string[]; // columns excluded from * via DuckDB EXCLUDE clause
  filters: FilterClause[];
  groupByColumns: string[];
  aggregations: AggregationClause[];
  having: HavingClause[]; // HAVING conditions — only active when groupByColumns is non-empty
  orderBy: OrderByClause[];
  joins: JoinClause[];
  lookups: LookupClause[];
  /** Tables stacked below the base query via UNION [ALL] BY NAME. */
  unions: UnionClause[];
  /** false → UNION ALL [BY NAME] (default, efficient); true → UNION [BY NAME] (dedupe). */
  unionDeduplicate: boolean;
  /** Column alignment strategy — auto uses position-based UNION when schemas match exactly. */
  unionAlignMode: UnionAlignMode;
  windowFunctions?: WindowFunctionClause[];
  distinct: boolean;
  limit: number; // 0 = no LIMIT in generated SQL (server controls pagination)
}

// ── Smart Suggestion Types ───────────────────────────────────────────

export interface TransformSuggestion {
  label: string; // e.g. "Fill 847 nulls (15.2%)"
  description: string; // e.g. "Replace NULLs with empty string"
  sqlFragment: string; // e.g. 'COALESCE("name", \'\')'
  column: string;
  type: 'coalesce' | 'upper' | 'trim' | 'round' | 'date_trunc' | 'group_by' | 'filter';
}

// ── Initialization ───────────────────────────────────────────────────

let _nextFilterId = 1;
let _nextJoinId = 1;
let _nextHavingId = 1;
let _nextUnionId = 1;

function nextFilterId(): string { return `f_${_nextFilterId++}`; }
function nextJoinId(): string { return `j_${_nextJoinId++}`; }
function nextHavingId(): string { return `h_${_nextHavingId++}`; }
function nextUnionId(): string { return `u_${_nextUnionId++}`; }

/** Create an initial TransformState for a table. */
export function initStateFromTable(tableName: string, limit = 0): TransformState {
  return {
    baseTable: tableName,
    selectColumns: [], // SELECT *
    excludeColumns: [],
    filters: [],
    groupByColumns: [],
    aggregations: [],
    having: [],
    orderBy: [],
    joins: [],
    lookups: [],
    unions: [],
    unionDeduplicate: false,
    unionAlignMode: 'auto',
    windowFunctions: [],
    distinct: false,
    limit,
  };
}

// ── SQL Generation ───────────────────────────────────────────────────

export interface UnionSchemaHints {
  /** Output column names of the base query (in order), when known. */
  baseColumns?: string[];
  /** Full column list per workspace table name. */
  tableColumns?: Record<string, string[]>;
}

export interface BuildSqlOptions {
  unionHints?: UnionSchemaHints;
}

function branchEffectiveColumns(
  clause: UnionClause,
  hints?: UnionSchemaHints,
): string[] | null {
  const explicit = (clause.columns || []).filter(Boolean);
  if (explicit.length > 0) return explicit;
  if (clause.schemaSnapshot?.length) return clause.schemaSnapshot;
  const hinted = hints?.tableColumns?.[clause.table];
  return hinted?.length ? hinted : null;
}

function resolveUnionAlignMode(
  state: TransformState,
  _baseColumns: string[] | null,
  _branchColumns: Array<string[] | null>,
): 'by_name' | 'by_position' {
  const mode = normalizeUnionAlignMode(state.unionAlignMode);
  if (mode === 'by_position') return 'by_position';
  // Auto aligns by name — workspace column lists can differ from DuckDB physical order.
  return 'by_name';
}

/** Map stored align mode to UI values; legacy `by_name` is treated as auto. */
export function normalizeUnionAlignMode(mode: string | undefined): UnionAlignMode {
  return mode === 'by_position' ? 'by_position' : 'auto';
}

function resolveUnionOperator(deduplicate: boolean, align: 'by_name' | 'by_position'): string {
  if (deduplicate) {
    return align === 'by_position' ? 'UNION' : 'UNION BY NAME';
  }
  return align === 'by_position' ? 'UNION ALL' : 'UNION ALL BY NAME';
}

function computeUnionProjectionColumns(
  unions: UnionClause[],
  baseColumns: string[] | null,
  branchColumns: Array<string[] | null>,
): string[] | null {
  const hasExplicitProjection = unions.some((clause) => (clause.columns || []).length > 0);
  if (!hasExplicitProjection) return null;

  const ordered: string[] = [];
  const seen = new Set<string>();
  const appendColumns = (columns: string[] | null | undefined): void => {
    for (const column of columns || []) {
      if (seen.has(column)) continue;
      seen.add(column);
      ordered.push(column);
    }
  };

  unions.forEach((clause, index) => {
    const explicit = (clause.columns || []).filter(Boolean);
    appendColumns(explicit.length > 0 ? explicit : branchColumns[index]);
  });
  if (ordered.length === 0) return null;
  if (!baseColumns?.length) return null;

  const baseSet = new Set(baseColumns);
  const sharedWithBase = ordered.filter((column) => baseSet.has(column));
  return sharedWithBase.length > 0 ? sharedWithBase : null;
}

function wrapSqlWithColumnProjection(sql: string, columns: string[]): string {
  const selectList = columns.map(quoteIdent).join(', ');
  return `SELECT ${selectList}\nFROM (\n${sql}\n) AS ${quoteIdent('__union_base')}`;
}

function buildUnionBranchSql(
  clause: UnionClause,
  options: { projectionColumns?: string[] | null },
): string {
  const explicit = (clause.columns || []).filter(Boolean);
  const columnList = explicit.length > 0
    ? explicit
    : (options.projectionColumns || []);
  const selectList = columnList.length > 0
    ? columnList.map(quoteIdent).join(', ')
    : '*';
  return `SELECT ${selectList}\nFROM ${quoteIdent(clause.table)}`;
}

export function buildUnionSchemaHints(
  state: TransformState,
  baseColumns: string[],
  workspaceTables: Array<{ name: string; columns: Array<{ name: string }> }>,
): UnionSchemaHints {
  const tableColumns: Record<string, string[]> = {};
  for (const table of workspaceTables) {
    tableColumns[table.name] = table.columns.map((column) => column.name);
  }
  const resolvedBaseColumns = baseColumns.length > 0
    ? baseColumns
    : tableColumns[state.baseTable] || [];
  return {
    baseColumns: resolvedBaseColumns,
    tableColumns,
  };
}

function appendOrderAndLimitToSql(
  sql: string,
  state: TransformState,
  options: { scope: 'inner' | 'outer' },
): string {
  let result = sql;
  if (state.orderBy.length > 0) {
    const orderParts = state.orderBy.map((o) => {
      if (options.scope === 'outer') {
        return `${quoteIdent(o.column)} ${o.direction}`;
      }
      const qualifier = getColumnQualifier(o.column, state);
      const originalCol = getOriginalCol(o.column, state);
      const ref = qualifier ? `${quoteIdent(qualifier)}.${quoteIdent(originalCol)}` : quoteIdent(o.column);
      return `${ref} ${o.direction}`;
    });
    result += `\nORDER BY ${orderParts.join(', ')}`;
  } else if ((state.windowFunctions ?? []).length > 0) {
    const fallbackOrderParts = buildDeterministicWindowOrderBy(state, { scope: options.scope });
    if (fallbackOrderParts.length > 0) {
      result += `\nORDER BY ${fallbackOrderParts.join(', ')}`;
    }
  }
  if (state.limit > 0) {
    result += `\nLIMIT ${state.limit}`;
  }
  return result;
}

/** Build a DuckDB-compatible SQL string from TransformState. */
export function buildSqlFromState(state: TransformState, options?: BuildSqlOptions): string {
  const parts: string[] = [];
  const windowClauses = state.windowFunctions ?? [];
  const windowOutputColumns = new Set(windowClauses.map((wf) => wf.outputCol));
  const hasUnions = state.unions.length > 0;
  const hasPostWindowFilters = state.filters.some((filter) => windowOutputColumns.has(filter.column));
  const deferOrderAndLimit = hasPostWindowFilters || hasUnions;
  const requiresUnifiedOuterWhere = hasCrossBucketOrConnector(state.filters, windowOutputColumns);
  const carrierAliasByColumn = new Map<string, string>();
  if (requiresUnifiedOuterWhere) {
    for (const column of collectNonWindowFilterColumns(state.filters, windowOutputColumns)) {
      carrierAliasByColumn.set(column, `__wf_filter_${carrierAliasByColumn.size + 1}`);
    }
  }
  const renames = state.selectColumns.filter((c) => c.isRename);
  const replacements = state.selectColumns.filter(
    (c) => c.isReplacement && !c.isRename && isBaseTableColumn(c.alias, state),
  );
  const overlappingRenames = renames.filter(
    (r) => replacements.some((replacement) => quoteIdent(replacement.alias) === r.expression),
  );
  const standaloneRenames = renames.filter(
    (r) => !replacements.some((replacement) => quoteIdent(replacement.alias) === r.expression),
  );

  // SELECT clause
  const selectParts: string[] = [];
  const hasJoins = state.joins.length > 0 || (state.lookups || []).length > 0;
  const baseAlias = hasJoins ? 't' : null;
  const baseRef = baseAlias ? `${baseAlias}.` : '';

  if (state.groupByColumns.length > 0 || state.aggregations.length > 0) {
    // GROUP BY mode: emit grouped columns + aggregations
    for (const col of state.groupByColumns) {
      const ref = hasJoins ? `${baseAlias}.${quoteIdent(col)}` : quoteIdent(col);
      selectParts.push(ref);
    }
    for (const agg of state.aggregations) {
      const ref = hasJoins ? `${baseAlias}.${quoteIdent(agg.column)}` : quoteIdent(agg.column);
      const expr = agg.func === 'COUNT_DISTINCT'
        ? `COUNT(DISTINCT ${ref})`
        : `${agg.func}(${ref})`;
      selectParts.push(`${expr} AS ${quoteIdent(agg.alias)}`);
    }
  } else if (state.selectColumns.length > 0) {
    // Explicit column expressions — uses DuckDB's REPLACE and EXCLUDE clauses
    // to modify columns in-place rather than creating duplicates.
    const hasWildcard = state.selectColumns.some((c) => c.expression === '*' && !c.alias);
    for (const sc of state.selectColumns) {
      if (sc.expression === '*' && !sc.alias) {
        // Wildcard passthrough — render * with EXCLUDE and REPLACE modifiers
        let starExpr = hasJoins ? `${baseAlias}.*` : '*';

        // EXCLUDE clause: columns dropped or renamed away from *
        const starExcludes = baseTableExcludeColumns(state);
        if (starExcludes.length > 0) {
          starExpr += ` EXCLUDE (${starExcludes.map(quoteIdent).join(', ')})`;
        }

        // REPLACE clause: columns transformed in-place (UPPER, CAST, etc.)
        if (replacements.length > 0) {
          const replaceExprs = replacements.map(
            (r) => `${r.expression} AS ${quoteIdent(r.alias)}`,
          );
          starExpr += ` REPLACE (${replaceExprs.join(', ')})`;
        }

        // RENAME clause: columns renamed in-place (preserves position)
        if (standaloneRenames.length > 0) {
          const renameExprs = standaloneRenames.map(
            (r) => `${r.expression} AS ${quoteIdent(r.alias)}`,
          );
          starExpr += ` RENAME (${renameExprs.join(', ')})`;
        }

        selectParts.push(starExpr);
      } else if (sc.isReplacement && hasWildcard && !isBaseTableColumn(sc.alias, state)) {
        // Lookup/join columns are not in t.* — DuckDB REPLACE cannot target them.
        selectParts.push(`${sc.expression} AS ${quoteIdent(sc.alias)}`);
      } else if ((sc.isReplacement || sc.isRename) && !hasWildcard) {
        // Materialized column list (e.g. after reorder): emit transforms directly.
        selectParts.push(`${sc.expression} AS ${quoteIdent(sc.alias)}`);
      } else if (!sc.isReplacement && !sc.isRename) {
        // Non-replacement/rename entries: additions (new computed columns)
        selectParts.push(`${sc.expression} AS ${quoteIdent(sc.alias)}`);
      }
      // With wildcard *, base-table isReplacement/isRename entries are handled in REPLACE/RENAME above.
    }
  } else if (
    (state.lookups || []).length > 0
    || state.joins.some((join) => (join.columns || []).length > 0)
  ) {
    // If we have lookups/join output columns but no explicit SELECT list, keep base table wildcard
    let starExpr = hasJoins ? `${baseAlias}.*` : '*';
    const starExcludes = baseTableExcludeColumns(state);
    if (starExcludes.length > 0) {
      starExpr += ` EXCLUDE (${starExcludes.map(quoteIdent).join(', ')})`;
    }
    selectParts.push(starExpr);
  }

  // Lookup columns — bring in selected columns from reference tables
  const isGroupedQuery = state.groupByColumns.length > 0 || state.aggregations.length > 0;
  const explicitSelectAliases = state.selectColumns.length > 0
    ? new Set(state.selectColumns.map((sc) => sc.alias))
    : null;
  if (!isGroupedQuery) {
    state.joins.forEach((join, idx) => {
      for (const col of join.columns || []) {
        const outputAlias = join.columnAliases?.[col] ?? col;
        if (explicitSelectAliases?.has(outputAlias) || state.excludeColumns.includes(outputAlias)) continue;
        const jAlias = `j${idx + 1}`;
        selectParts.push(`${quoteIdent(jAlias)}.${quoteIdent(col)} AS ${quoteIdent(outputAlias)}`);
      }
    });
    (state.lookups || []).forEach((lookup, idx) => {
      const lAlias = `L${idx + 1}`;
      for (const col of lookup.columns) {
        // Use "L#_col" for absolute collision safety in output
        const outputAlias = lookup.columnAliases?.[col] ?? `L${idx + 1}_${col}`;
        if (explicitSelectAliases?.has(outputAlias) || state.excludeColumns.includes(outputAlias)) continue;
        selectParts.push(`${quoteIdent(lAlias)}.${quoteIdent(col)} AS ${quoteIdent(outputAlias)}`);
      }
    });
  }

  // Build SELECT line
  const distinctStr = state.distinct ? 'DISTINCT ' : '';
  if (selectParts.length > 0) {
    for (const wf of windowClauses) {
      const windowExpr = buildWindowExpression(wf, { baseAlias });
      if (!windowExpr) continue;
      selectParts.push(`${windowExpr} AS ${quoteIdent(wf.outputCol)}`);
    }
    if (carrierAliasByColumn.size > 0) {
      for (const [column, alias] of Array.from(carrierAliasByColumn.entries())) {
        const qualifier = getColumnQualifier(column, state);
        const originalCol = getOriginalCol(column, state);
        const ref = qualifier ? `${quoteIdent(qualifier)}.${quoteIdent(originalCol)}` : quoteIdent(column);
        selectParts.push(`${ref} AS ${quoteIdent(alias)}`);
      }
    }
    parts.push(`SELECT ${distinctStr}${selectParts.join(', ')}`);
  } else {
    // SELECT * (possibly table-qualified for joins), with EXCLUDE if needed
    let star = hasJoins ? `${baseAlias}.*` : '*';
    const starExcludes = baseTableExcludeColumns(state);
    if (starExcludes.length > 0) {
      star += ` EXCLUDE (${starExcludes.map(quoteIdent).join(', ')})`;
    }
    if (windowClauses.length > 0) {
      const wfSelect = windowClauses
        .map((wf) => {
          const windowExpr = buildWindowExpression(wf, { baseAlias });
          return windowExpr ? `${windowExpr} AS ${quoteIdent(wf.outputCol)}` : null;
        })
        .filter((part): part is string => Boolean(part));
      if (carrierAliasByColumn.size > 0) {
        for (const [column, alias] of Array.from(carrierAliasByColumn.entries())) {
          const qualifier = getColumnQualifier(column, state);
          const originalCol = getOriginalCol(column, state);
          const ref = qualifier ? `${quoteIdent(qualifier)}.${quoteIdent(originalCol)}` : quoteIdent(column);
          wfSelect.push(`${ref} AS ${quoteIdent(alias)}`);
        }
      }
      if (wfSelect.length > 0) {
        parts.push(`SELECT ${distinctStr}${star}, ${wfSelect.join(', ')}`);
      } else {
        parts.push(`SELECT ${distinctStr}${star}`);
      }
    } else {
      if (carrierAliasByColumn.size > 0) {
        const passthrough = Array.from(carrierAliasByColumn.entries()).map(([column, alias]) => {
          const qualifier = getColumnQualifier(column, state);
          const originalCol = getOriginalCol(column, state);
          const ref = qualifier ? `${quoteIdent(qualifier)}.${quoteIdent(originalCol)}` : quoteIdent(column);
          return `${ref} AS ${quoteIdent(alias)}`;
        });
        parts.push(`SELECT ${distinctStr}${star}, ${passthrough.join(', ')}`);
      } else {
      parts.push(`SELECT ${distinctStr}${star}`);
      }
    }
  }

  // FROM clause
  parts.push(`FROM ${quoteIdent(state.baseTable)}${baseAlias ? ` AS ${baseAlias}` : ''}`);

  // JOIN clauses
  state.joins.forEach((j, idx) => {
    const hasOutputColumns = (j.columns || []).length > 0;
    const jAlias = `j${idx + 1}`;
    const rightTableRef = hasOutputColumns ? quoteIdent(jAlias) : quoteIdent(j.table);
    const onClauses = j.conditions.map((c) => {
      const leftRef = `${baseRef ? baseRef : `${quoteIdent(state.baseTable)}.`}${quoteIdent(c.leftColumn)}`;
      const rightRef = `${rightTableRef}.${quoteIdent(c.rightColumn)}`;
      return `${leftRef} = ${rightRef}`;
    });
    const joinTarget = hasOutputColumns
      ? `${quoteIdent(j.table)} AS ${quoteIdent(jAlias)}`
      : quoteIdent(j.table);
    parts.push(`${j.joinType} JOIN ${joinTarget} ON ${onClauses.join(' AND ')}`);
  });

  // Lookup JOINs
  (state.lookups || []).forEach((l, idx) => {
    const lookupAlias = `L${idx + 1}`;
    const joinType = l.keepUnmatched ? 'LEFT' : 'INNER';
    const qualifier = getColumnQualifier(l.sourceKey, state) || baseAlias || quoteIdent(state.baseTable);
    const originalColName = getOriginalCol(l.sourceKey, state);

    if (l.matchBehavior === 'first' && !l.isUnique) {
      /**
       * First Match (Deterministic): uses a subquery with ROW_NUMBER() to pick the first row
       * for each key. This is more portable than DISTINCT ON and allows explicit ordering if needed.
       */
      const subquery = `(SELECT *, ROW_NUMBER() OVER (PARTITION BY ${quoteIdent(l.refKey)}) as __rn FROM ${quoteIdent(l.refTable)})`;
      parts.push(`${joinType} JOIN ${subquery} AS ${quoteIdent(lookupAlias)} ON ${quoteIdent(qualifier)}.${quoteIdent(originalColName)} = ${quoteIdent(lookupAlias)}.${quoteIdent(l.refKey)} AND ${quoteIdent(lookupAlias)}.__rn = 1`);
    } else {
      // Simple (Unique) or All Matches: plain join
      parts.push(`${joinType} JOIN ${quoteIdent(l.refTable)} AS ${quoteIdent(lookupAlias)} ON ${quoteIdent(qualifier)}.${quoteIdent(originalColName)} = ${quoteIdent(lookupAlias)}.${quoteIdent(l.refKey)}`);
    }
  });

  // WHERE clause — split filters into pre-window and post-window passes.
  // Window outputs (e.g. ROW_NUMBER aliases) cannot be referenced in the
  // same SELECT's WHERE in DuckDB; those must run in an outer query.
  const { preWindowFilters, postWindowFilters } = partitionFiltersForWindowPasses(state.filters, windowOutputColumns);
  if (!requiresUnifiedOuterWhere && preWindowFilters.length > 0) {
    const whereStr = buildWhereExpression(preWindowFilters, (filter) => buildFilterCondition(filter, state));
    if (whereStr) {
      parts.push(`WHERE ${whereStr}`);
    }
  }

  // GROUP BY clause
  if (state.groupByColumns.length > 0) {
    const groupRefs = state.groupByColumns.map((col) => {
      return hasJoins ? `${baseAlias}.${quoteIdent(col)}` : quoteIdent(col);
    });
    parts.push(`GROUP BY ${groupRefs.join(', ')}`);
  }

  // HAVING clause
  if (state.groupByColumns.length > 0 && state.having.length > 0) {
    const havingParts = state.having.map((h) => {
      const aggExpr = h.aggregateFunc === 'COUNT_STAR'
        ? 'COUNT(*)'
        : h.aggregateFunc === 'COUNT_DISTINCT'
          ? `COUNT(DISTINCT ${quoteIdent(h.column)})`
          : `${h.aggregateFunc}(${quoteIdent(h.column)})`;
      return `${aggExpr} ${h.operator} ${/^-?\d+(\.\d+)?$/.test(h.value.trim()) ? h.value.trim() : `'${h.value.replace(/'/g, "''")}'`}`;
    });
    parts.push(`HAVING ${havingParts.join(' AND ')}`);
  }

  // ORDER BY/LIMIT are deferred when post-window filters or UNION branches
  // are present so ranking/pagination apply to the final combined result.
  if (!deferOrderAndLimit) {
    if (state.orderBy.length > 0) {
      const orderParts = state.orderBy.map((o) => {
        const qualifier = getColumnQualifier(o.column, state);
        const originalCol = getOriginalCol(o.column, state);
        const ref = qualifier ? `${quoteIdent(qualifier)}.${quoteIdent(originalCol)}` : quoteIdent(o.column);
        return `${ref} ${o.direction}`;
      });
      parts.push(`ORDER BY ${orderParts.join(', ')}`);
    } else if (windowClauses.length > 0) {
      const fallbackOrderParts = buildDeterministicWindowOrderBy(state);
      if (fallbackOrderParts.length > 0) {
        parts.push(`ORDER BY ${fallbackOrderParts.join(', ')}`);
      }
    }

    // LIMIT clause — only if explicitly set (server controls pagination otherwise)
    if (state.limit > 0) {
      parts.push(`LIMIT ${state.limit}`);
    }
  }

  let sql = parts.join('\n');
  if (requiresUnifiedOuterWhere && hasPostWindowFilters) {
    const outerWhere = buildWhereExpression(state.filters, (filter) => {
      if (windowOutputColumns.has(filter.column)) {
        return buildOuterFilterCondition(filter);
      }
      const carrierAlias = carrierAliasByColumn.get(filter.column);
      const ref = carrierAlias ? quoteIdent(carrierAlias) : quoteIdent(filter.column);
      return buildFilterConditionFromRef(filter, ref);
    });
    sql = `SELECT *\nFROM (\n${sql}\n) AS ${quoteIdent('__windowed')}\nWHERE ${outerWhere}`;
    if (carrierAliasByColumn.size > 0) {
      const carrierAliases = Array.from(carrierAliasByColumn.values()).map(quoteIdent).join(', ');
      sql = `SELECT * EXCLUDE (${carrierAliases})\nFROM (\n${sql}\n) AS ${quoteIdent('__windowed_filtered')}`;
    }
  } else if (postWindowFilters.length > 0) {
    const outerWhere = buildWhereExpression(postWindowFilters, buildOuterFilterCondition);
    sql = `SELECT *\nFROM (\n${sql}\n) AS ${quoteIdent('__windowed')}\nWHERE ${outerWhere}`;
  }
  if (hasPostWindowFilters && !hasUnions) {
    if (state.orderBy.length > 0) {
      const outerOrderParts = state.orderBy.map((o) => `${quoteIdent(o.column)} ${o.direction}`);
      sql += `\nORDER BY ${outerOrderParts.join(', ')}`;
    } else if (windowClauses.length > 0) {
      const fallbackOrderParts = buildDeterministicWindowOrderBy(state, { scope: 'outer' });
      if (fallbackOrderParts.length > 0) {
        sql += `\nORDER BY ${fallbackOrderParts.join(', ')}`;
      }
    }
    if (state.limit > 0) {
      sql += `\nLIMIT ${state.limit}`;
    }
  }

  if (hasUnions) {
    const unionHints = options?.unionHints;
    const branchColumns = state.unions.map((clause) => branchEffectiveColumns(clause, unionHints));
    const baseColumns = unionHints?.baseColumns?.length ? unionHints.baseColumns : null;
    const alignMode = resolveUnionAlignMode(state, baseColumns, branchColumns);
    const unionKeyword = resolveUnionOperator(state.unionDeduplicate, alignMode);
    const projectionBaseColumns = unionHints?.tableColumns?.[state.baseTable]?.length
      ? unionHints.tableColumns[state.baseTable]
      : baseColumns;
    const projectionColumns = computeUnionProjectionColumns(state.unions, projectionBaseColumns, branchColumns);
    const baseSql = projectionColumns?.length
      ? wrapSqlWithColumnProjection(sql, projectionColumns)
      : sql;
    const unionBranches = state.unions.map((clause) => buildUnionBranchSql(clause, { projectionColumns }));
    const wrappedBranches = [`(\n${baseSql}\n)`, ...unionBranches.map((branch) => `(\n${branch}\n)`)];
    sql = wrappedBranches.join(`\n${unionKeyword}\n`);
    if (deferOrderAndLimit) {
      sql = appendOrderAndLimitToSql(sql, state, { scope: 'outer' });
    }
  }

  if (overlappingRenames.length === 0) {
    return sql;
  }

  const outerRenameExprs = overlappingRenames.map(
    (r) => `${r.expression} AS ${quoteIdent(r.alias)}`,
  );
  return `SELECT * RENAME (${outerRenameExprs.join(', ')})\nFROM (\n${sql}\n) AS ${quoteIdent('__transformed')}`;
}

function buildWindowExpression(
  wf: WindowFunctionClause,
  options: { baseAlias: string | null },
): string | null {
  const rankingFns = new Set<WindowFunctionName>(['ROW_NUMBER', 'RANK', 'DENSE_RANK', 'NTILE']);
  const orderSensitiveFns = new Set<WindowFunctionName>([
    'ROW_NUMBER',
    'RANK',
    'DENSE_RANK',
    'NTILE',
    'LAG',
    'LEAD',
    'FIRST_VALUE',
    'LAST_VALUE',
  ]);
  const requiresValueFns = new Set<WindowFunctionName>([
    'RUNNING_SUM',
    'SUM',
    'AVG',
    'COUNT',
    'MIN',
    'MAX',
    'LAG',
    'LEAD',
    'FIRST_VALUE',
    'LAST_VALUE',
  ]);
  const qualify = (col: string): string => options.baseAlias ? `${options.baseAlias}.${quoteIdent(col)}` : quoteIdent(col);

  let fnCall: string;
  if (wf.func === 'NTILE') {
    const buckets = wf.ntileBuckets;
    if (!Number.isInteger(buckets) || (buckets ?? 0) <= 0) {
      return null;
    }
    fnCall = `NTILE(${buckets})`;
  } else if (wf.func === 'RUNNING_COUNT') {
    fnCall = wf.valueCol ? `COUNT(${qualify(wf.valueCol)})` : 'COUNT(*)';
  } else if (wf.func === 'RUNNING_SUM') {
    if (!wf.valueCol) {
      return null;
    }
    fnCall = `SUM(${qualify(wf.valueCol)})`;
  } else if (wf.func === 'COUNT' && !wf.valueCol) {
    fnCall = 'COUNT(*)';
  } else if (rankingFns.has(wf.func)) {
    fnCall = `${wf.func}()`;
  } else if (requiresValueFns.has(wf.func)) {
    if (!wf.valueCol) {
      return null;
    }
    fnCall = `${wf.func}(${qualify(wf.valueCol)})`;
  } else {
    fnCall = `${wf.func}()`;
  }

  const overParts: string[] = [];
  if (wf.partitionBy.length > 0) {
    overParts.push(`PARTITION BY ${wf.partitionBy.map(qualify).join(', ')}`);
  }
  const explicitFrame = wf.frame?.trim();
  // SUM, AVG, MIN, MAX without an explicit frame are global partition aggregates
  // (same value on every row). Adding ORDER BY activates SQL's default RANGE
  // frame (UNBOUNDED PRECEDING to CURRENT ROW), turning them into running
  // aggregates — each row gets its own running total/average/min/max instead
  // of the partition-wide value. RUNNING_SUM/RUNNING_COUNT intentionally DO
  // include ORDER BY because they are meant to be running aggregates.
  const globalAggFns = new Set<WindowFunctionName>(['SUM', 'AVG', 'MIN', 'MAX', 'COUNT']);
  const isGlobalAgg = globalAggFns.has(wf.func) && !explicitFrame;
  if (wf.orderBy?.col && !isGlobalAgg) {
    overParts.push(`ORDER BY ${qualify(wf.orderBy.col)} ${wf.orderBy.dir}`);
  } else if (orderSensitiveFns.has(wf.func)) {
    return null;
  }
  if (explicitFrame) {
    overParts.push(`ROWS BETWEEN ${explicitFrame}`);
  } else if (wf.func === 'LAST_VALUE' && wf.orderBy?.col) {
    // Default SQL frame ends at CURRENT ROW; for LAST_VALUE we want
    // the final value from the full partition unless user overrides.
    overParts.push('ROWS BETWEEN UNBOUNDED PRECEDING AND UNBOUNDED FOLLOWING');
  } else if ((wf.func === 'RUNNING_SUM' || wf.func === 'RUNNING_COUNT') && wf.orderBy?.col) {
    // Running aggregates should always use ROWS semantics so each row gets a
    // stepwise cumulative value even when ORDER BY has duplicate values.
    overParts.push('ROWS BETWEEN UNBOUNDED PRECEDING AND CURRENT ROW');
  }
  return `${fnCall} OVER (${overParts.join(' ')})`;
}

function buildDeterministicWindowOrderBy(
  state: TransformState,
  options: { scope: 'inner' | 'outer' } = { scope: 'inner' },
): string[] {
  const { scope } = options;
  const seen = new Set<string>();
  const orderParts: string[] = [];

  const addOrderPart = (part: string): void => {
    if (seen.has(part)) return;
    seen.add(part);
    orderParts.push(part);
  };

  /** Inner SELECT: qualify disambiguated refs. Outer (__windowed): output names only. */
  const refForOrderColumn = (col: string): string => {
    if (scope === 'outer') {
      return quoteIdent(col);
    }
    const qualifier = getColumnQualifier(col, state);
    const originalCol = getOriginalCol(col, state);
    return qualifier ? `${quoteIdent(qualifier)}.${quoteIdent(originalCol)}` : quoteIdent(col);
  };

  for (const wf of state.windowFunctions ?? []) {
    for (const col of wf.partitionBy) {
      addOrderPart(`${refForOrderColumn(col)} ASC`);
    }

    if (wf.orderBy?.col) {
      addOrderPart(`${refForOrderColumn(wf.orderBy.col)} ${wf.orderBy.dir}`);
    }

    // Include output aliases to stabilize rows when keys tie.
    addOrderPart(`${quoteIdent(wf.outputCol)} ASC`);
  }

  return orderParts;
}

function getColumnQualifier(col: string, state: TransformState): string | null {
  for (let i = 0; i < (state.lookups || []).length; i++) {
    const lookup = state.lookups[i];
    for (const sourceCol of lookup.columns) {
      const outputAlias = lookup.columnAliases?.[sourceCol] ?? `L${i + 1}_${sourceCol}`;
      if (outputAlias === col) {
        return `L${i + 1}`;
      }
    }
    const prefix = `L${i + 1}_`;
    if (col.startsWith(prefix)) {
      return `L${i + 1}`;
    }
    // Note: We no longer match bare column names here to ensure base table columns
    // are preferred unless explicitly qualified by the lookup output alias.
  }
  for (let i = 0; i < state.joins.length; i++) {
    const join = state.joins[i];
    for (const sourceCol of join.columns || []) {
      const renamedAlias = join.columnAliases?.[sourceCol];
      if (renamedAlias === col) {
        return `j${i + 1}`;
      }
    }
  }
  const hasJoins = state.joins.length > 0 || (state.lookups || []).length > 0;
  return hasJoins ? 't' : null;
}

function getOriginalCol(col: string, state: TransformState): string {
  for (let i = 0; i < (state.lookups || []).length; i++) {
    const lookup = state.lookups[i];
    for (const sourceCol of lookup.columns) {
      const outputAlias = lookup.columnAliases?.[sourceCol] ?? `L${i + 1}_${sourceCol}`;
      if (outputAlias === col) {
        return sourceCol;
      }
    }
    const prefix = `L${i + 1}_`;
    if (col.startsWith(prefix)) {
      return col.substring(prefix.length);
    }
  }
  for (let i = 0; i < state.joins.length; i++) {
    const join = state.joins[i];
    for (const sourceCol of join.columns || []) {
      const renamedAlias = join.columnAliases?.[sourceCol];
      if (renamedAlias === col) {
        return sourceCol;
      }
    }
  }
  return col;
}

/** True when col is part of the base table wildcard (t.*), not a lookup/join column. */
function isBaseTableColumn(col: string, state: TransformState): boolean {
  const qualifier = getColumnQualifier(col, state);
  return qualifier === null || qualifier === 't';
}

/** DuckDB EXCLUDE only applies to base-table wildcard columns. */
function baseTableExcludeColumns(state: TransformState): string[] {
  return state.excludeColumns.filter((col) => isBaseTableColumn(col, state));
}

function buildFilterCondition(f: FilterClause, state: TransformState): string {
  const ref = resolveFilterColumnRef(f.column, state);
  return buildFilterConditionFromRef(f, ref);
}

/** Resolve the SQL expression to filter on.
 *  For lookup/join columns only, honour selectColumns transforms so filters match displayed values.
 *  Base-table columns keep the original qualifier/ident resolution unchanged. */
function resolveFilterColumnRef(column: string, state: TransformState): string {
  const qualifier = getColumnQualifier(column, state);
  const originalCol = getOriginalCol(column, state);
  const defaultRef = qualifier
    ? `${quoteIdent(qualifier)}.${quoteIdent(originalCol)}`
    : quoteIdent(column);

  if (isBaseTableColumn(column, state)) {
    return defaultRef;
  }

  const selectCol = state.selectColumns.find((c) => c.alias === column);
  if (selectCol && selectCol.expression !== '*' && selectCol.expression !== defaultRef) {
    return `(${selectCol.expression})`;
  }

  return defaultRef;
}

function buildOuterFilterCondition(f: FilterClause): string {
  const ref = quoteIdent(f.column);
  return buildFilterConditionFromRef(f, ref);
}

function isNullLiteral(value: string): boolean {
  return /^null$/i.test(value.trim());
}

function buildMissingValueCondition(ref: string): string {
  return `(${ref} IS NULL OR TRIM(CAST(${ref} AS VARCHAR)) = '')`;
}

function buildPresentValueCondition(ref: string): string {
  return `(${ref} IS NOT NULL AND TRIM(CAST(${ref} AS VARCHAR)) <> '')`;
}

function normalizeNullFilterOperator(
  operator: FilterOperator,
  value: string,
): FilterOperator {
  if (!isNullLiteral(value)) return operator;
  if (operator === '=') return 'IS NULL';
  if (operator === '!=') return 'IS NOT NULL';
  return operator;
}

function normalizeFilterClause<T extends Pick<FilterClause, 'operator' | 'value'>>(filter: T): T {
  return filter;
}

function buildFilterConditionFromRef(f: FilterClause, ref: string): string {
  const normalized = normalizeFilterClause(f);
  let condition: string;
  switch (normalized.operator) {
    case 'IS NULL':
      condition = buildMissingValueCondition(ref);
      break;
    case 'IS NOT NULL':
      condition = buildPresentValueCondition(ref);
      break;
    case 'IN': {
      const values = normalized.value.split(',').map((v) => `'${v.trim().replace(/'/g, "''")}'`);
      condition = `${ref} IN (${values.join(', ')})`;
      break;
    }
    case 'LIKE':
      condition = `${ref} ${normalized.operator} '${normalized.value.replace(/'/g, "''")}'`;
      break;
    case 'NOT LIKE':
      condition = `${ref} NOT LIKE '${normalized.value.replace(/'/g, "''")}'`;
      if (!normalized.negate) {
        condition = `(${condition} OR ${buildMissingValueCondition(ref)})`;
      }
      break;
    default:
      if (/^-?\d+(\.\d+)?$/.test(normalized.value.trim())) {
         // Use TRY_CAST so columns that are VARCHAR but contain mostly numeric values
        // (e.g. budget col with a literal "NULL" string in some rows) don't throw a
        // conversion error. TRY_CAST returns NULL for non-castable values, which
        // correctly excludes those rows from the numeric comparison.
        condition = `TRY_CAST(${ref} AS DOUBLE) ${normalized.operator} ${normalized.value.trim()}`;
      } else {
        condition = `${ref} ${normalized.operator} '${normalized.value.replace(/'/g, "''")}'`;
      }
  }
  return normalized.negate ? `NOT (${condition})` : condition;
}

function partitionFiltersForWindowPasses(
  filters: FilterClause[],
  windowOutputColumns: Set<string>,
): { preWindowFilters: FilterClause[]; postWindowFilters: FilterClause[] } {
  const preWindowFilters: FilterClause[] = [];
  const postWindowFilters: FilterClause[] = [];
  let previousBucket: 'pre' | 'post' | null = null;

  for (const filter of filters) {
    const bucket: 'pre' | 'post' = windowOutputColumns.has(filter.column) ? 'post' : 'pre';
    const target = bucket === 'post' ? postWindowFilters : preWindowFilters;
    const isFirstInBucket = target.length === 0;
    const normalizedLogicOp = isFirstInBucket
      ? undefined
      : previousBucket === bucket
        ? (filter.logicOp || 'AND')
        : 'AND';
    target.push({ ...filter, logicOp: normalizedLogicOp });
    previousBucket = bucket;
  }

  return { preWindowFilters, postWindowFilters };
}

function buildWhereExpression(
  filters: FilterClause[],
  renderCondition: (filter: FilterClause) => string,
): string {
  if (filters.length === 0) return '';
  let expression = renderCondition(filters[0]);
  for (let i = 1; i < filters.length; i++) {
    const op = filters[i].logicOp || 'AND';
    expression += ` ${op} ${renderCondition(filters[i])}`;
  }
  return expression;
}

function hasCrossBucketOrConnector(filters: FilterClause[], windowOutputColumns: Set<string>): boolean {
  for (let i = 1; i < filters.length; i++) {
    const logicOp = filters[i].logicOp || 'AND';
    if (logicOp !== 'OR') continue;
    const prevIsWindow = windowOutputColumns.has(filters[i - 1].column);
    const currentIsWindow = windowOutputColumns.has(filters[i].column);
    if (prevIsWindow !== currentIsWindow) return true;
  }
  return false;
}

function collectNonWindowFilterColumns(
  filters: FilterClause[],
  windowOutputColumns: Set<string>,
): string[] {
  const ordered: string[] = [];
  for (const filter of filters) {
    if (windowOutputColumns.has(filter.column)) continue;
    if (!ordered.includes(filter.column)) ordered.push(filter.column);
  }
  return ordered;
}

function toSqlLiteral(value: string): string {
  const trimmed = value.trim();
  if (/^null$/i.test(trimmed)) return 'NULL';
  if (/^(true|false)$/i.test(trimmed)) return trimmed.toUpperCase();
  if (/^-?\d+(\.\d+)?$/.test(trimmed)) return trimmed;
  return `'${value.replace(/'/g, "''")}'`;
}

function toTypedSqlLiteral(value: string, columnType?: string): string {
  const trimmed = value.trim();
  if (/^null$/i.test(trimmed)) return 'NULL';

  const normalizedType = (columnType || '').toLowerCase();
  const isNumericType = /int|bigint|double|float|decimal|numeric|smallint|tinyint/i.test(normalizedType);
  const isBooleanType = /bool/i.test(normalizedType);
  const isDateType = /^date$/i.test(normalizedType);
  const isTimestampType = /timestamp|datetime/i.test(normalizedType);

  if (isNumericType && /^-?\d+(\.\d+)?$/.test(trimmed)) return trimmed;
  if (isBooleanType && /^(true|false)$/i.test(trimmed)) return trimmed.toUpperCase();
  if (isDateType) {
    // Non-ISO literals (e.g. 2023/06/04) must stay as VARCHAR for try_strptime on a later cast.
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      return `CAST('${value.replace(/'/g, "''")}' AS DATE)`;
    }
    return `'${value.replace(/'/g, "''")}'`;
  }
  if (isTimestampType) return `CAST('${value.replace(/'/g, "''")}' AS TIMESTAMP)`;

  return `'${value.replace(/'/g, "''")}'`;
}

function unwrapCoalesceSource(expression: string): string {
  const trimmed = expression.trim();
  if (!trimmed.startsWith('COALESCE(') || !trimmed.endsWith(')')) {
    return expression;
  }

  const inner = trimmed.slice('COALESCE('.length, -1);
  let depth = 0;
  for (let i = 0; i < inner.length; i++) {
    const char = inner[i];
    if (char === '(') depth += 1;
    if (char === ')') depth -= 1;
    if (char === ',' && depth === 0) {
      return inner.slice(0, i).trim();
    }
  }

  return expression;
}

function resolveColumnSourceExpr(
  state: TransformState,
  column: string,
  existingColumn?: { expression: string },
): string {
  const bareIdent = quoteIdent(column);
  if (existingColumn?.expression && existingColumn.expression !== '*' && existingColumn.expression !== bareIdent) {
    return existingColumn.expression;
  }
  const qualifier = getColumnQualifier(column, state);
  const originalCol = getOriginalCol(column, state);
  return qualifier
    ? `${quoteIdent(qualifier)}.${quoteIdent(originalCol)}`
    : quoteIdent(originalCol);
}

/** Resolve quoted column refs in a user expression to prior transform expressions. */
function resolveExpressionColumnRefs(state: TransformState, expression: string): string {
  return expression.replace(/"((?:[^"]|"")*)"/g, (match, rawName: string) => {
    const columnName = rawName.replace(/""/g, '"');
    const existingColumn = state.selectColumns.find((c) => c.alias === columnName);
    const bareIdent = quoteIdent(columnName);
    if (!existingColumn?.expression || existingColumn.expression === '*' || existingColumn.expression === bareIdent) {
      return match;
    }
    const sourceExpr = resolveColumnSourceExpr(state, columnName, existingColumn);
    return `(${sourceExpr})`;
  });
}

function resolveCaseColumnRef(state: TransformState, column: string): string {
  for (let i = state.selectColumns.length - 1; i >= 0; i--) {
    const selectColumn = state.selectColumns[i];
    if (selectColumn.alias !== column || selectColumn.expression === '*') continue;
    return `(${selectColumn.expression})`;
  }
  return quoteIdent(column);
}

function buildCaseCondition(clause: CaseWhenClause, state: TransformState): string {
  const ref = resolveCaseColumnRef(state, clause.column); 
  const normalizedOperator = normalizeNullFilterOperator(clause.operator, clause.value);

  switch (normalizedOperator) {
    case 'IS NULL':
      return buildMissingValueCondition(ref);
    case 'IS NOT NULL':
      return buildPresentValueCondition(ref);
    case 'IN': {
      const values = clause.value.split(',').map((value) => toSqlLiteral(value.trim()));
      return `${ref} IN (${values.join(', ')})`;
    }
    case 'LIKE':
    case 'NOT LIKE':
      return `${ref} ${normalizedOperator} '${clause.value.replace(/'/g, "''")}'`;
    default:
      return `${ref} ${normalizedOperator} ${toSqlLiteral(clause.value)}`;
  }
}

// ── State Mutators (pure) ────────────────────────────────────────────

export function applyFilter(state: TransformState, filter: Omit<FilterClause, 'id'>): TransformState {
  const normalized = normalizeFilterClause(filter);
  return {
    ...state,
    filters: [...state.filters, { ...normalized, id: nextFilterId() }],
  };
}

export function removeFilter(state: TransformState, filterId: string): TransformState {
  return {
    ...state,
    filters: state.filters.filter((f) => f.id !== filterId),
  };
}

export function updateFilter(state: TransformState, filterId: string, updates: Partial<FilterClause>): TransformState {
  return {
    ...state,
    filters: state.filters.map((f) => {
      if (f.id !== filterId) return f;
      return normalizeFilterClause({ ...f, ...updates });
    }),
  };
}

export function applySort(state: TransformState, column: string, direction: SortDir): TransformState {
  // Replace existing sort on same column, or add new
  const existing = state.orderBy.findIndex((o) => o.column === column);
  if (existing >= 0) {
    const updated = [...state.orderBy];
    updated[existing] = { column, direction };
    return { ...state, orderBy: updated };
  }
  return {
    ...state,
    orderBy: [...state.orderBy, { column, direction }],
  };
}

export function removeSort(state: TransformState, column: string): TransformState {
  return {
    ...state,
    orderBy: state.orderBy.filter((o) => o.column !== column),
  };
}

export function applyGroupBy(
  state: TransformState,
  columns: string[],
  aggregations: AggregationClause[],
  having: HavingClause[] = [],
): TransformState {
  return {
    ...state,
    groupByColumns: columns,
    aggregations,
    having,
    // Clear explicit select columns — GROUP BY takes over the SELECT clause
    selectColumns: [],
  };
}

export function clearGroupBy(state: TransformState): TransformState {
  return {
    ...state,
    groupByColumns: [],
    aggregations: [],
    having: [],
  };
}

export function applyHaving(state: TransformState, clause: Omit<HavingClause, 'id'>): TransformState {
  return {
    ...state,
    having: [...state.having, { ...clause, id: nextHavingId() }],
  };
}

export function removeHaving(state: TransformState, havingId: string): TransformState {
  return { ...state, having: state.having.filter((h) => h.id !== havingId) };
}

export function applyJoin(state: TransformState, join: Omit<JoinClause, 'id'>): TransformState {
  return {
    ...state,
    joins: [...state.joins, { ...join, columns: join.columns ?? [], id: nextJoinId() }],
  };
}

export function updateJoin(state: TransformState, joinId: string, updates: Partial<Omit<JoinClause, 'id'>>): TransformState {
  return {
    ...state,
    joins: state.joins.map((j) => j.id === joinId ? { ...j, ...updates } : j),
  };
}

export function removeJoin(state: TransformState, joinId: string): TransformState {
  return {
    ...state,
    joins: state.joins.filter((j) => j.id !== joinId),
  };
}

export function applyUnion(
  state: TransformState,
  union: Omit<UnionClause, 'id'>,
  options?: { deduplicate?: boolean; alignMode?: UnionAlignMode; schemaSnapshot?: string[] },
): TransformState {
  const alreadyStacked = new Set(state.unions.map((u) => u.table));
  if (alreadyStacked.has(union.table) || union.table === state.baseTable) {
    return state;
  }
  return {
    ...state,
    unions: [...state.unions, {
      ...union,
      columns: union.columns ?? [],
      schemaSnapshot: options?.schemaSnapshot ?? union.schemaSnapshot,
      id: nextUnionId(),
    }],
    unionDeduplicate: options?.deduplicate ?? state.unionDeduplicate,
    unionAlignMode: options?.alignMode ?? state.unionAlignMode,
  };
}

export function updateUnion(
  state: TransformState,
  unionId: string,
  updates: Partial<Omit<UnionClause, 'id'>>,
): TransformState {
  return {
    ...state,
    unions: state.unions.map((u) => (u.id === unionId ? { ...u, ...updates } : u)),
  };
}

export function removeUnion(state: TransformState, unionId: string): TransformState {
  const unions = state.unions.filter((u) => u.id !== unionId);
  return {
    ...state,
    unions,
    unionDeduplicate: unions.length === 0 ? false : state.unionDeduplicate,
    unionAlignMode: unions.length === 0 ? 'auto' : state.unionAlignMode,
  };
}

export function setUnionDeduplicate(state: TransformState, deduplicate: boolean): TransformState {
  return { ...state, unionDeduplicate: deduplicate };
}

export function setUnionAlignMode(state: TransformState, alignMode: UnionAlignMode): TransformState {
  return { ...state, unionAlignMode: normalizeUnionAlignMode(alignMode) };
}

let lookupCounter = 0;
function nextLookupId(): string {
  lookupCounter++;
  return `l_${lookupCounter}`;
}

export function applyLookup(state: TransformState, lookup: Omit<LookupClause, 'id'>): TransformState {
  return {
    ...state,
    lookups: [...(state.lookups || []), { ...lookup, id: nextLookupId() }],
  };
}

export function updateLookup(state: TransformState, lookupId: string, updates: Partial<Omit<LookupClause, 'id'>>): TransformState {
  return {
    ...state,
    lookups: (state.lookups || []).map((l) => l.id === lookupId ? { ...l, ...updates } : l),
  };
}

export function removeLookup(state: TransformState, lookupId: string): TransformState {
  return {
    ...state,
    lookups: (state.lookups || []).filter((l) => l.id !== lookupId),
  };
}

export function applyAddColumn(
  state: TransformState,
  expression: string,
  alias: string,
  existingColumnNames: string[] = [],
): TransformState {
  const resolvedExpression = resolveExpressionColumnRefs(state, expression);
  const isReplacement = existingColumnNames.includes(alias);
  const existingIdx = state.selectColumns.findIndex((c) => c.alias === alias);
  if (existingIdx >= 0) {
    const updated = [...state.selectColumns];
    updated[existingIdx] = { expression: resolvedExpression, alias, isReplacement };
    return {
      ...state,
      selectColumns: updated,
    };
  }
  // When selectColumns is empty (meaning SELECT *), preserve all existing columns
  // by inserting a wildcard entry before the new computed column.
  const existingCols = state.selectColumns.length === 0
    ? [{ expression: '*', alias: '' }]
    : state.selectColumns;
  return {
    ...state,
    selectColumns: [...existingCols, { expression: resolvedExpression, alias, isReplacement }],
  };
}

export function removeSelectColumn(state: TransformState, alias: string): TransformState {
  return {
    ...state,
    selectColumns: state.selectColumns.filter((c) => c.alias !== alias),
  };
}

export function applyDistinct(state: TransformState, enabled: boolean): TransformState {
  return { ...state, distinct: enabled };
}

export function applyRename(state: TransformState, oldName: string, newName: string): TransformState {
  const renameColumnRef = (column: string): string => (column === oldName ? newName : column);
  const applyDependentRename = (nextState: TransformState): TransformState => ({
    ...nextState,
    filters: nextState.filters.map((filter) => ({
      ...filter,
      column: renameColumnRef(filter.column),
    })),
    orderBy: nextState.orderBy.map((sort) => ({
      ...sort,
      column: renameColumnRef(sort.column),
    })),
  });

  for (let i = 0; i < state.joins.length; i++) {
    const join = state.joins[i];
    for (const sourceCol of join.columns || []) {
      const currentAlias = join.columnAliases?.[sourceCol] ?? sourceCol;
      if (currentAlias !== oldName) continue;
      const columnAliases = { ...(join.columnAliases || {}), [sourceCol]: newName };
      const joins = state.joins.map((entry, idx) => (
        idx === i ? { ...entry, columnAliases } : entry
      ));
      const selectIdx = state.selectColumns.findIndex((c) => c.alias === oldName);
      const selectColumns = selectIdx >= 0
        ? state.selectColumns.map((sc, idx) => (
          idx === selectIdx ? { ...sc, alias: newName } : sc
        ))
        : state.selectColumns;
      return applyDependentRename({ ...state, joins, selectColumns });
    }
  }

  for (let i = 0; i < (state.lookups || []).length; i++) {
    const lookup = state.lookups[i];
    for (const sourceCol of lookup.columns) {
      const currentAlias = lookup.columnAliases?.[sourceCol] ?? `L${i + 1}_${sourceCol}`;
      if (currentAlias !== oldName) continue;
      const columnAliases = { ...(lookup.columnAliases || {}), [sourceCol]: newName };
      const lookups = (state.lookups || []).map((entry, idx) => (
        idx === i ? { ...entry, columnAliases } : entry
      ));
      const selectIdx = state.selectColumns.findIndex((c) => c.alias === oldName);
      const selectColumns = selectIdx >= 0
        ? state.selectColumns.map((sc, idx) => (
          idx === selectIdx ? { ...sc, alias: newName } : sc
        ))
        : state.selectColumns;
      return applyDependentRename({ ...state, lookups, selectColumns });
    }
  }

  const existingIdx = state.selectColumns.findIndex((c) => c.alias === oldName);
  if (existingIdx >= 0) {
    const updated = [...state.selectColumns];
    const existingColumn = updated[existingIdx];

    if (existingColumn.isReplacement && !existingColumn.isRename) {
      const renameIdx = updated.findIndex(
        (c) => c.isRename && c.expression === quoteIdent(oldName),
      );

      if (renameIdx >= 0) {
        updated[renameIdx] = { expression: quoteIdent(oldName), alias: newName, isRename: true };
      } else {
        updated.push({ expression: quoteIdent(oldName), alias: newName, isRename: true });
      }

      return applyDependentRename({ ...state, selectColumns: updated });
    }

    // For non-replacement entries, update the alias directly.
    updated[existingIdx] = { ...existingColumn, alias: newName, isReplacement: false };
    return applyDependentRename({ ...state, selectColumns: updated });
  }
  // SELECT * mode: use RENAME for in-place rename (preserves column position)
  // Generates: SELECT * RENAME ("old_name" AS "new_name") FROM ...
  const existingCols = state.selectColumns.length === 0
    ? [{ expression: '*', alias: '' }]
    : state.selectColumns;
  return applyDependentRename({
    ...state,
    selectColumns: [
      ...existingCols,
      { expression: quoteIdent(oldName), alias: newName, isRename: true },
    ],
    // No excludeColumns change — REPLACE handles position preservation
  });
}

export function applyDropColumn(state: TransformState, columnName: string): TransformState {
  const nextFilters = state.filters.filter((filter) => filter.column !== columnName);

  // If we already have explicit columns, remove the one with this alias
  if (state.selectColumns.length > 0) {
    // Check if the column being dropped is a newly created column (has alias and not a replacement/rename)
    const columnToRemove = state.selectColumns.find((c) => c.alias === columnName);
    const isNewlyCreatedColumn = columnToRemove && !columnToRemove.isReplacement && !columnToRemove.isRename;
    
    // Remove from selectColumns
    const filtered = state.selectColumns.filter((c) => c.alias !== columnName);
    
    // If a * entry exists, only add to excludeColumns if it's NOT a newly created column
    const hasWildcard = state.selectColumns.some((c) => c.expression === '*' && !c.alias);
    const shouldExclude = hasWildcard && !isNewlyCreatedColumn;
    const shouldSuppressLookupOrJoin = !isNewlyCreatedColumn && !isBaseTableColumn(columnName, state);

    // Base-table renames use the original column name for t.* EXCLUDE.
    // Lookup/join columns keep the current output alias for suppression.
    let columnNameToExclude = columnName;
    if (
      columnToRemove?.isRename
      && columnToRemove.expression
      && isBaseTableColumn(columnName, state)
    ) {
      // Extract original column name from quoted expression
      columnNameToExclude = columnToRemove.expression.slice(1, -1).replace(/""/g, '"');
    }

    return {
      ...state,
      selectColumns: filtered,
      filters: nextFilters,
      excludeColumns: (shouldExclude || shouldSuppressLookupOrJoin)
        ? [
          ...state.excludeColumns.filter((c) => c !== columnNameToExclude && c !== columnName),
          columnNameToExclude,
        ]
        : state.excludeColumns,
    };
  }
  // In SELECT * mode: use DuckDB EXCLUDE to remove the column from *
  return {
    ...state,
    filters: nextFilters,
    excludeColumns: [...state.excludeColumns.filter((c) => c !== columnName), columnName],
  };
}

export function applyColumnFunction(
  state: TransformState,
  column: string,
  func: string,
  alias?: string,
): TransformState {
  const outputAlias = alias || column;
  const sourceColumn = state.selectColumns.find((c) => c.alias === column);
  const sourceExpr = resolveColumnSourceExpr(state, column, sourceColumn);
  const expression = `${func}(${sourceExpr})`;
  const existingIdx = state.selectColumns.findIndex((c) => c.alias === outputAlias);
  if (existingIdx >= 0) {
    const existing = state.selectColumns[existingIdx];
    const updated = [...state.selectColumns];
    updated[existingIdx] = {
      ...existing,
      expression,
      alias: outputAlias,
      isReplacement: !!existing.isReplacement,
      isRename: false,
    };
    return { ...state, selectColumns: updated };
  }
  // When alias matches the source column, this is an in-place replacement
  const isReplacement = outputAlias === column;
  // Preserve * when selectColumns is empty
  const existingCols = state.selectColumns.length === 0
    ? [{ expression: '*', alias: '' }]
    : state.selectColumns;
  return {
    ...state,
    selectColumns: [...existingCols, { expression, alias: outputAlias, isReplacement }],
  };
}

export function applyCoalesce(
  state: TransformState,
  column: string,
  defaultValue = '',
): TransformState {
  const existingIdx = state.selectColumns.findIndex((c) => c.alias === column);
  const existingColumn = existingIdx >= 0 ? state.selectColumns[existingIdx] : undefined;
  const sourceExpr = resolveColumnSourceExpr(
    state,
    column,
    existingColumn
      ? { expression: unwrapCoalesceSource(existingColumn.expression) }
      : undefined,
  );
  const fillExpr = `'${defaultValue.replace(/'/g, "''")}'`;
  const expression = `COALESCE(CAST(${sourceExpr} AS VARCHAR), ${fillExpr})`;

  if (existingIdx >= 0) {
    const updated = [...state.selectColumns];
    updated[existingIdx] = {
      expression,
      alias: column,
      isReplacement: !!existingColumn?.isReplacement,
    };
    return { ...state, selectColumns: updated };
  }

  const existingCols = state.selectColumns.length === 0
    ? [{ expression: '*', alias: '' }]
    : state.selectColumns;
  return {
    ...state,
    selectColumns: [...existingCols, { expression, alias: column, isReplacement: true }],
  };
}

/** VARCHAR / CHAR / TEXT — not numeric types whose names contain other substrings. */
function isSqlStringLikeType(sqlType: string | undefined): boolean {
  if (!sqlType) return false;
  const u = sqlType.trim().toUpperCase();
  if (u.includes('VARCHAR') || u.includes('NVARCHAR') || u.includes('TEXT') || u === 'STRING') return true;
  if (u.startsWith('CHAR') || u.startsWith('NCHAR')) return true;
  return false;
}

/**
 * INTEGER / BIGINT / floating types: NULL and 0 → FALSE; any other numeric value (including 1) → TRUE.
 * Excludes INTERVAL. SERIAL* types match via INT / SERIAL substring rules.
 */
function isSqlIntegralOrFloatForBooleanCast(sqlType: string | undefined): boolean {
  if (!sqlType) return false;
  const u = sqlType.trim().toUpperCase();
  if (u.includes('INTERVAL')) return false;
  if (u.includes('INT') || u.includes('SERIAL')) return true;
  if (u.includes('DOUBLE') || u.includes('FLOAT') || u === 'REAL') return true;
  return false;
}

function isSqlTemporalType(sqlType: string | undefined): boolean {
  if (!sqlType) return false;
  const u = sqlType.trim().toUpperCase();
  return u === 'DATE' || u.startsWith('TIMESTAMP');
}

function castIntegralOrFloatToBooleanSql(sourceExpr: string): string {
  return `CASE WHEN ${sourceExpr} IS NULL THEN FALSE WHEN ${sourceExpr} = 0 THEN FALSE ELSE TRUE END`;
}

/** BOOLEAN from string vs integral/float vs temporal vs unknown source type (TRY_CAST). */
function castToBooleanSql(sourceExpr: string, sourceColumnType: string | undefined): string {
  if (isSqlStringLikeType(sourceColumnType)) {
    const trimmed = `TRIM(CAST(${sourceExpr} AS VARCHAR))`;
    return `CASE WHEN ${sourceExpr} IS NULL THEN FALSE WHEN ${trimmed} = '' THEN FALSE ELSE TRUE END`;
  }
  if (isSqlIntegralOrFloatForBooleanCast(sourceColumnType)) {
    return castIntegralOrFloatToBooleanSql(sourceExpr);
  }
  if (isSqlTemporalType(sourceColumnType)) {
    return `CASE WHEN ${sourceExpr} IS NULL THEN FALSE ELSE TRUE END`;
  }
  return `TRY_CAST(${sourceExpr} AS BOOLEAN)`;
}

const VARCHAR_TO_TEMPORAL_FORMATS = [
  '%Y-%m-%d %H:%M:%S',
  '%Y-%m-%d',
  '%Y/%m/%d',
  '%d-%m-%Y',
  '%d/%m/%Y',
  '%m/%d/%Y',
  '%d-%b-%Y',
  '%d-%B-%Y',
] as const;

function expressionContainsStringLiteral(expression: string): boolean {
  return /'(?:[^']|'')*'/i.test(expression);
}

function castStringToTemporalSql(sourceExpr: string, targetType: string): string {
  const formatArray = VARCHAR_TO_TEMPORAL_FORMATS.map((format) => `'${format}'`).join(', ');
  const parsed = `try_strptime(CAST(${sourceExpr} AS VARCHAR), [${formatArray}])`;
  return `${parsed}::${targetType.trim().toUpperCase()}`;
}

function shouldUseStringTemporalCast(
  targetNorm: string,
  inferredSourceType: string | undefined,
  sourceExpression: string | undefined,
): boolean {
  if (targetNorm !== 'DATE' && targetNorm !== 'TIMESTAMP') return false;
  if (isSqlStringLikeType(inferredSourceType)) return true;
  if (!sourceExpression) return false;
  return /COALESCE\(/i.test(sourceExpression) && expressionContainsStringLiteral(sourceExpression);
}

export function applyCast(
  state: TransformState,
  column: string,
  targetType: string,
  sourceColumnType?: string,
): TransformState {
  const existingIdx = state.selectColumns.findIndex((c) => c.alias === column);
  const existingColumn = existingIdx >= 0 ? state.selectColumns[existingIdx] : undefined;
  const sourceExpr = resolveColumnSourceExpr(state, column, existingColumn);
  
  // For computed columns without type info, infer type from expression
  let inferredSourceType = sourceColumnType;
  if (!inferredSourceType && existingColumn?.expression) {
    const temporalMatch = existingColumn.expression.match(/::(DATE|TIMESTAMP\w*)\s*$/i);
    if (temporalMatch) {
      inferredSourceType = temporalMatch[1].toUpperCase();
    } else if (expressionContainsStringLiteral(existingColumn.expression)) {
      // If expression contains string literals, treat as string type
      inferredSourceType = 'VARCHAR';
    }
  }
  
  const targetNorm = targetType.trim().toUpperCase();
  const expression =
    targetNorm === 'BOOLEAN'
      ? castToBooleanSql(sourceExpr, inferredSourceType)
      : shouldUseStringTemporalCast(targetNorm, inferredSourceType, existingColumn?.expression)
        ? castStringToTemporalSql(sourceExpr, targetType)
        : `TRY_CAST(${sourceExpr} AS ${targetType})`;
  if (existingIdx >= 0) {
    const updated = [...state.selectColumns];
    updated[existingIdx] = {
      ...existingColumn,
      expression,
      alias: column,
      isReplacement: !!existingColumn?.isReplacement,
      outputType: targetNorm,
    };
    return { ...state, selectColumns: updated };
  }
  // Preserve * when selectColumns is empty
  const existingCols = state.selectColumns.length === 0
    ? [{ expression: '*', alias: '' }]
    : state.selectColumns;
  return {
    ...state,
    selectColumns: [...existingCols, { expression, alias: column, isReplacement: true, outputType: targetNorm }],
  };
}

export function applyReplaceMissing(
  state: TransformState,
  columns: string[],
  strategy: ReplaceMissingStrategy,
  constantValue = '',
  columnTypeMap: Record<string, string> = {},
): TransformState {
  const uniqueColumns = Array.from(new Set(columns.filter(Boolean)));
  if (uniqueColumns.length === 0) return state;

  let nextState = state;

  for (const column of uniqueColumns) {
    const existingIdx = nextState.selectColumns.findIndex((c) => c.alias === column);
    const existingColumn = existingIdx >= 0 ? nextState.selectColumns[existingIdx] : undefined;
    const sourceExpr = resolveColumnSourceExpr(
      nextState,
      column,
      existingColumn
        ? { expression: unwrapCoalesceSource(existingColumn.expression) }
        : undefined,
    );

    let fillExpr: string;
    switch (strategy) {
      case 'constant':
        fillExpr = toTypedSqlLiteral(constantValue, columnTypeMap[column]);
        break;
      case 'mean':
        fillExpr = `AVG(${sourceExpr}) OVER ()`;
        break;
      case 'median':
        fillExpr = `MEDIAN(${sourceExpr}) OVER ()`;
        break;
      case 'mode':
        fillExpr = `MODE(${sourceExpr}) OVER ()`;
        break;
      case 'forward_fill': {
        const rowOrderExpr = nextState.joins.length > 0 || (nextState.lookups || []).length > 0
          ? 't.rowid'
          : 'rowid';
        fillExpr = `LAST_VALUE(${sourceExpr} IGNORE NULLS) OVER (ORDER BY ${rowOrderExpr} ROWS BETWEEN UNBOUNDED PRECEDING AND CURRENT ROW)`;
        break;
      }
      default:
        fillExpr = toTypedSqlLiteral(constantValue, columnTypeMap[column]);
        break;
    }

    const fillIsStringLiteral = strategy === 'constant' && fillExpr.trim().startsWith("'");
    const coalesceSourceExpr = fillIsStringLiteral
      ? `CAST(${sourceExpr} AS VARCHAR)`
      : sourceExpr;

    const expression = `COALESCE(${coalesceSourceExpr}, ${fillExpr})`;

    if (existingIdx >= 0) {
      const updated = [...nextState.selectColumns];
      const existingColumn = nextState.selectColumns[existingIdx];
      // Preserve isReplacement — computed columns (isReplacement: false) must stay
      // as SELECT additions, not DuckDB * REPLACE (which only works for base-table columns).
      updated[existingIdx] = {
        expression,
        alias: column,
        isReplacement: !!existingColumn.isReplacement,
      };
      nextState = { ...nextState, selectColumns: updated };
      continue;
    }

    const existingCols = nextState.selectColumns.length === 0
      ? [{ expression: '*', alias: '' }]
      : nextState.selectColumns;
    nextState = {
      ...nextState,
      selectColumns: [...existingCols, { expression, alias: column, isReplacement: true }],
    };
  }

  return nextState;
}

function extractStrftimeOutputFormat(expression: string): string | undefined {
  const match = expression.match(/^STRFTIME\('((?:[^']|'')*)',\s*.+\)$/i);
  if (!match) return undefined;
  return match[1].replace(/''/g, "'");
}

function strftimeFormatHasTimeTokens(format: string): boolean {
  return /%[HIMS]/.test(format) || /%(?:z|Z)/.test(format);
}

function inferStrftimeOutputType(format: string): 'DATE' | 'TIMESTAMP' {
  return strftimeFormatHasTimeTokens(format) ? 'TIMESTAMP' : 'DATE';
}

export function selectColumnExpressionHasDateTimeStrftime(expression: string): boolean {
  const match = expression.match(/^STRFTIME\s*\(\s*'((?:[^']|'')*)'/i);
  if (!match) return false;
  return strftimeFormatHasTimeTokens(match[1].replace(/''/g, "'"));
}

export function applyDateFormat(
  state: TransformState,
  column: string,
  outputFormat: string,
  inputFormat?: string,
): TransformState {
  const existingIdx = state.selectColumns.findIndex((c) => c.alias === column);
  const existingColumn = existingIdx >= 0 ? state.selectColumns[existingIdx] : undefined;
  const escapedOutputFormat = outputFormat.replace(/'/g, "''");
  const trimmedInputFormat = inputFormat?.trim();
  const escapedInputFormat = trimmedInputFormat?.replace(/'/g, "''");
  const baseExpr = resolveColumnSourceExpr(state, column, existingColumn);
  let sourceExpr = baseExpr;
  if (escapedInputFormat) {
    sourceExpr = `TRY_STRPTIME(CAST(${baseExpr} AS VARCHAR), '${escapedInputFormat}')`;
  } else {
    const previousOutputFormat = extractStrftimeOutputFormat(baseExpr);
    if (previousOutputFormat) {
      const escapedPreviousFormat = previousOutputFormat.replace(/'/g, "''");
      sourceExpr = `TRY_STRPTIME(CAST(${baseExpr} AS VARCHAR), '${escapedPreviousFormat}')`;
    } else {
      const outputType = existingColumn ? inferSelectColumnOutputType(existingColumn) : null;
      const isTemporal = outputType === 'DATE' || outputType === 'TIMESTAMP' || outputType === 'TIMESTAMPTZ'
        || /::\s*(DATE|TIMESTAMP(?:TZ)?)\b/i.test(baseExpr)
        || /\btry_strptime\s*\(/i.test(baseExpr)
        || (/^TRY_CAST\(/i.test(baseExpr) && /\s+AS\s+(DATE|TIMESTAMP\w*)\)/i.test(baseExpr));
      if (!isTemporal) {
        const formatArray = VARCHAR_TO_TEMPORAL_FORMATS.map((format) => `'${format}'`).join(', ');
        sourceExpr = `try_strptime(CAST(${baseExpr} AS VARCHAR), [${formatArray}])`;
      }
    }
  }
  const sourceOutputType = existingColumn ? inferSelectColumnOutputType(existingColumn) : null;
  const sourceIsDateOnly = sourceOutputType === 'DATE'
    || /::\s*DATE\b/i.test(sourceExpr);
  if (sourceIsDateOnly && strftimeFormatHasTimeTokens(escapedOutputFormat)) {
    sourceExpr = `CAST(${sourceExpr} AS TIMESTAMP)`;
  }
  const expression = `STRFTIME('${escapedOutputFormat}', ${sourceExpr})`;
  const formattedOutputType = inferStrftimeOutputType(outputFormat);

  if (existingIdx >= 0) {
    const updated = [...state.selectColumns];
    updated[existingIdx] = {
      expression,
      alias: column,
      isReplacement: !!existingColumn?.isReplacement,
      outputType: formattedOutputType,
    };
    return { ...state, selectColumns: updated };
  }

  const existingCols = state.selectColumns.length === 0
    ? [{ expression: '*', alias: '' }]
    : state.selectColumns;

  return {
    ...state,
    selectColumns: [...existingCols, { expression, alias: column, isReplacement: true, outputType: formattedOutputType }],
  };
}

function escapeSqlStringLiteral(value: string): string {
  return value.replace(/'/g, "''");
}

function escapeRegexLiteral(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Only normalize simple slash lists like (Yes/Y/true) — entire pattern, regex mode only. */
function normalizeRegexAlternationSlashes(pattern: string): string {
  const match = pattern.match(/^\(([^)]*)\)$/);
  if (!match) return pattern;

  const inner = match[1];
  if (!inner.includes('/') || inner.includes('|')) return pattern;

  const parts = inner.split('/');
  if (parts.length < 2) return pattern;
  if (!parts.every((part) => /^[A-Za-z_][A-Za-z0-9_]*$/.test(part))) return pattern;

  return `(${parts.join('|')})`;
}

export function applyReplacePattern(
  state: TransformState,
  column: string,
  findValue: string,
  replaceValue: string,
  options?: {
    regex?: boolean;
    caseSensitive?: boolean;
    wholeWord?: boolean;
  },
): TransformState {
  if (!findValue) return state;

  const existingIdx = state.selectColumns.findIndex((c) => c.alias === column);
  const existingColumn = existingIdx >= 0 ? state.selectColumns[existingIdx] : undefined;
  const sourceExpr = existingColumn ? existingColumn.expression : quoteIdent(column);
  const stringSourceExpr = `CAST(${sourceExpr} AS VARCHAR)`;
  const useRegex = options?.regex ?? false;
  const caseSensitive = options?.caseSensitive ?? true;
  const wholeWord = options?.wholeWord ?? false;
  const escapedFindValue = escapeSqlStringLiteral(findValue);
  const escapedReplaceValue = escapeSqlStringLiteral(replaceValue);

  let expression: string;
  if (!useRegex && caseSensitive && !wholeWord) {
    expression = `REPLACE(${stringSourceExpr}, '${escapedFindValue}', '${escapedReplaceValue}')`;
  } else {
    let pattern = useRegex
      ? normalizeRegexAlternationSlashes(findValue)
      : escapeRegexLiteral(findValue);
    if (wholeWord) {
      pattern = `\\b(?:${pattern})\\b`;
    }

    const escapedPattern = escapeSqlStringLiteral(pattern);
    const regexOptions = `g${caseSensitive ? '' : 'i'}`;
    expression = `REGEXP_REPLACE(${stringSourceExpr}, '${escapedPattern}', '${escapedReplaceValue}', '${regexOptions}')`;
  }

  if (existingIdx >= 0) {
    const updated = [...state.selectColumns];
    if (existingColumn?.isRename) {
      const renamedSourceColumn = existingColumn.expression.slice(1, -1).replace(/""/g, '"');
      const replacementIdx = updated.findIndex(
        (c) => c.alias === renamedSourceColumn && c.isReplacement && !c.isRename,
      );

      if (replacementIdx >= 0) {
        updated[replacementIdx] = { expression, alias: renamedSourceColumn, isReplacement: true };
      } else {
        updated.push({ expression, alias: renamedSourceColumn, isReplacement: true });
      }
    } else {
      updated[existingIdx] = {
        expression,
        alias: column,
        isReplacement: !!existingColumn?.isReplacement,
      };
    }
    return { ...state, selectColumns: updated };
  }

  const existingCols = state.selectColumns.length === 0
    ? [{ expression: '*', alias: '' }]
    : state.selectColumns;

  return {
    ...state,
    selectColumns: [...existingCols, { expression, alias: column, isReplacement: true }],
  };
}

export function applyCaseExpression(
  state: TransformState,
  alias: string,
  whenClauses: CaseWhenClause[],
  elseValue: string,
  existingColumnNames: string[] = [],
): TransformState {
  const expression = [
    'CASE',
    ...whenClauses.map((clause) => `WHEN ${buildCaseCondition(clause, state)} THEN ${toSqlLiteral(clause.result)}`),
    `ELSE ${toSqlLiteral(elseValue)}`,
    'END',
  ].join(' ');
  const existingIdx = state.selectColumns.findIndex((c) => c.alias === alias);
  if (existingIdx >= 0) {
    const existing = state.selectColumns[existingIdx];
    const updated = [...state.selectColumns];
    const isReplacement = !!existing.isReplacement;
    updated[existingIdx] = { expression, alias, isReplacement, isRename: false };
    return { ...state, selectColumns: updated };
  }

  const isReplacement = existingColumnNames.includes(alias);
  const existingCols = state.selectColumns.length === 0
    ? [{ expression: '*', alias: '' }]
    : state.selectColumns;
  return {
    ...state,
    selectColumns: [...existingCols, { expression, alias, isReplacement }],
  };
}

function splitSourceStringExpr(col: SelectColumn): string {
  const expr = col.expression;
  // Date-format transforms already produce VARCHAR via STRFTIME — split that string as-is.
  if (/^STRFTIME\s*\(/i.test(expr)) {
    return `CAST(${expr} AS VARCHAR)`;
  }
  const isTemporal = /::\s*(DATE|TIMESTAMP|TIMESTAMPTZ)\b/i.test(expr) || /\btry_strptime\s*\(/i.test(expr);
  if (!isTemporal) {
    return `CAST(${expr} AS VARCHAR)`;
  }
  const outputType = inferSelectColumnOutputType(col);
  const isDateOnly = outputType === 'DATE'
    || (!outputType && /::\s*DATE\b/i.test(expr));
  return isDateOnly
    ? `STRFTIME('%Y-%m-%d', ${expr})`
    : `STRFTIME('%Y-%m-%d %H:%M:%S', ${expr})`;
}

export function applySplitColumn(
  state: TransformState,
  column: string,
  delimiter: string,
  numOutputColumns: number,
): TransformState {
  const escapedDelim = delimiter.replace(/'/g, "''");
  const replacementIdx = state.selectColumns.findIndex(
    (c) => c.alias === column && c.isReplacement && !c.isRename,
  );
  const replacementColumn = replacementIdx >= 0
    ? state.selectColumns[replacementIdx]
    : undefined;
  const splitSourceExpr = replacementColumn
    ? splitSourceStringExpr(replacementColumn)
    : `CAST(${resolveColumnSourceExpr(state, column, replacementColumn)} AS VARCHAR)`;
  const baseCols = replacementIdx >= 0
    ? state.selectColumns.filter((_, idx) => idx !== replacementIdx)
    : state.selectColumns;
  const existingCols = baseCols.length === 0
    ? [{ expression: '*', alias: '' }]
    : baseCols;

  // Add source column to EXCLUDE, add split parts as new columns
  const splitCols: SelectColumn[] = [];
  for (let i = 1; i <= numOutputColumns; i++) {
    splitCols.push({
      expression: `string_split(${splitSourceExpr}, '${escapedDelim}')[${i}]`,
      alias: `${column}_${i}`,
    });
  }

  const nextExcludeColumns = isBaseTableColumn(column, state)
    ? [...state.excludeColumns.filter((c) => c !== column), column]
    : state.excludeColumns;

  return {
    ...state,
    selectColumns: [...existingCols, ...splitCols],
    excludeColumns: nextExcludeColumns,
  };
}

export function applyMergeColumns(
  state: TransformState,
  columns: string[],
  separator: string,
  alias: string,
  dropSources: boolean,
): TransformState {
  const escapedSep = separator.replace(/'/g, "''");
  const concatArgs = columns.map(quoteIdent).join(', ');
  const expression = `CONCAT_WS('${escapedSep}', ${concatArgs})`;

  const existingCols = state.selectColumns.length === 0
    ? [{ expression: '*', alias: '' }]
    : state.selectColumns;

  const newExclude = dropSources
    ? state.excludeColumns.concat(columns.filter((c) => !state.excludeColumns.includes(c)))
    : state.excludeColumns;

  return {
    ...state,
    selectColumns: [...existingCols, { expression, alias }],
    excludeColumns: newExclude,
  };
}

export function applyWindowFunction(
  state: TransformState,
  clause: WindowFunctionClause,
): TransformState {
  const existing = state.windowFunctions ?? [];
  const next = existing.filter((wf) => wf.outputCol !== clause.outputCol);
  return {
    ...state,
    windowFunctions: [...next, clause],
  };
}

export function removeWindowFunction(state: TransformState, outputCol: string): TransformState {
  return {
    ...state,
    windowFunctions: (state.windowFunctions ?? []).filter((wf) => wf.outputCol !== outputCol),
  };
}

/**
 * Extract substrings matching a regex pattern into a new column.
 * Generates: CASE WHEN REGEXP_MATCHES("col", 'pattern') THEN REGEXP_EXTRACT(...) ELSE NULL END AS "new_col"
 * With group index: REGEXP_EXTRACT("col", 'pattern', groupIndex) inside the CASE branch
 */
export function applyExtractPattern(
  state: TransformState,
  column: string,
  pattern: string,
  newColumnAlias: string,
  groupIndex?: number,
): TransformState {
  const escapedPattern = pattern.replace(/'/g, "''");
  const patternLit = `'${escapedPattern}'`;
  const sourceColumn = state.selectColumns.find((c) => c.alias === column);
  const colRef = resolveColumnSourceExpr(state, column, sourceColumn);
  const extractCall = groupIndex != null && groupIndex > 0
    ? `REGEXP_EXTRACT(${colRef}, ${patternLit}, ${groupIndex})`
    : `REGEXP_EXTRACT(${colRef}, ${patternLit})`;
  const expression = `CASE WHEN REGEXP_MATCHES(${colRef}, ${patternLit}) THEN ${extractCall} ELSE NULL END`;

  const existingIdx = state.selectColumns.findIndex((c) => c.alias === newColumnAlias);
  if (existingIdx >= 0) {
    const updated = [...state.selectColumns];
    const existing = updated[existingIdx];
    updated[existingIdx] = { expression, alias: newColumnAlias, isReplacement: !!existing.isReplacement };
    return { ...state, selectColumns: updated };
  }

  const existingCols = state.selectColumns.length === 0
    ? [{ expression: '*', alias: '' }]
    : state.selectColumns;

  const replacesSourceColumn = newColumnAlias.toLowerCase() === column.toLowerCase();

  return {
    ...state,
    selectColumns: [...existingCols, {
      expression,
      alias: replacesSourceColumn ? column : newColumnAlias,
      isReplacement: replacesSourceColumn,
    }],
  };
}

export type MoveColumnPosition = 'first' | 'last' | 'before' | 'after';

/**
 * Reorder columns by moving a column to a specific position.
 * Materializes the SELECT * into an explicit column list so order is preserved.
 *
 * @param allColumns - The current ordered visible column names (from result?.columns)
 * @param relativeColumn - Required for 'before' and 'after' positions
 */
export function applyMoveColumn(
  state: TransformState,
  columnToMove: string,
  position: MoveColumnPosition,
  allColumns: string[],
  relativeColumn?: string,
): TransformState {
  // Compute the new column order
  const without = allColumns.filter((c) => c !== columnToMove);
  let newOrder: string[];

  if (position === 'first') {
    newOrder = [columnToMove, ...without];
  } else if (position === 'last') {
    newOrder = [...without, columnToMove];
  } else if (position === 'before' && relativeColumn) {
    const idx = without.indexOf(relativeColumn);
    newOrder = idx >= 0
      ? [...without.slice(0, idx), columnToMove, ...without.slice(idx)]
      : [columnToMove, ...without];
  } else { // 'after'
    const idx = relativeColumn ? without.indexOf(relativeColumn) : -1;
    newOrder = idx >= 0
      ? [...without.slice(0, idx + 1), columnToMove, ...without.slice(idx + 1)]
      : [...without, columnToMove];
  }

  // Build a lookup map: alias → SelectColumn for existing explicit transforms.
  // This captures renames, replacements, and computed columns.
  const selectMap = new Map<string, SelectColumn>();
  for (const sc of state.selectColumns) {
    if (sc.alias) {
      selectMap.set(sc.alias, sc);
    }
  }

  // Build an explicit SELECT column list from the new order.
  // Strip isReplacement/isRename flags so buildSqlFromState renders each entry
  // as a standalone `expression AS alias` (no REPLACE/RENAME clause needed
  // when there is no wildcard * in the list).
  const newSelectColumns: SelectColumn[] = newOrder.map((col) => {
    const existing = selectMap.get(col);
    if (existing && existing.expression !== '*') {
      return {
        expression: existing.expression,
        alias: col,
        ...(existing.outputType ? { outputType: existing.outputType } : {}),
      };
    }
    const qualifier = getColumnQualifier(col, state);
    if (qualifier) {
      const originalCol = getOriginalCol(col, state);
      return {
        expression: `${quoteIdent(qualifier)}.${quoteIdent(originalCol)}`,
        alias: col,
      };
    }
    return { expression: quoteIdent(col), alias: col };
  });

  return {
    ...state,
    selectColumns: newSelectColumns,
    // Excluded columns are absent from newOrder, so we can clear the set.
    excludeColumns: [],
  };
}

// ── Smart Suggestions ────────────────────────────────────────────────

/**
 * Generate contextual transform suggestions from column profile data.
 * Uses top_values for heuristic detection of whitespace/case issues.
 */
export function suggestTransforms(
  profile: WorkspaceColumnProfile,
  columnName: string,
): TransformSuggestion[] {
  const suggestions: TransformSuggestion[] = [];
  const isText = /varchar|text|string/i.test(profile.dtype);
  const isNumeric = /int|bigint|double|float|decimal|numeric/i.test(profile.dtype);
  const isTimestamp = /timestamp/i.test(profile.dtype) && !/date$/i.test(profile.dtype);

  // 1. Fill nulls
  if (profile.null_count > 0) {
    let pct: string;
    if (profile.null_pct != null && !isNaN(profile.null_pct)) {
      // null_pct is a 0-1 ratio from the backend
      pct = (profile.null_pct * 100).toFixed(1);
    } else if (profile.count > 0) {
      pct = ((profile.null_count / profile.count) * 100).toFixed(1);
    } else {
      pct = '?';
    }
    suggestions.push({
      label: `Fill ${profile.null_count.toLocaleString()} nulls (${pct}%)`,
      description: 'Replace NULLs with default value',
      sqlFragment: `COALESCE(${quoteIdent(columnName)}, '')`,
      column: columnName,
      type: 'coalesce',
    });
  }

  // 2. Mixed case (check top_values for text columns)
  if (isText && profile.top_values && profile.top_values.length > 0) {
    const hasUpper = profile.top_values.some((tv) => /[A-Z]/.test(tv.value));
    const hasLower = profile.top_values.some((tv) => /[a-z]/.test(tv.value));
    if (hasUpper && hasLower) {
      suggestions.push({
        label: 'Standardize to UPPER',
        description: 'Convert all values to uppercase for consistency',
        sqlFragment: `UPPER(${quoteIdent(columnName)})`,
        column: columnName,
        type: 'upper',
      });
    }
  }

  // 3. Trim whitespace (check top_values for leading/trailing spaces)
  if (isText && profile.top_values && profile.top_values.length > 0) {
    const hasWhitespace = profile.top_values.some((tv) => tv.value !== tv.value.trim());
    if (hasWhitespace) {
      const count = profile.top_values.filter((tv) => tv.value !== tv.value.trim()).length;
      suggestions.push({
        label: `Trim whitespace (${count} found in top values)`,
        description: 'Remove leading and trailing whitespace',
        sqlFragment: `TRIM(${quoteIdent(columnName)})`,
        column: columnName,
        type: 'trim',
      });
    }
  }

  // 4. Round decimals (floating point columns)
  if (isNumeric && /double|float/i.test(profile.dtype) && profile.mean != null) {
    suggestions.push({
      label: 'Round to 2 decimals',
      description: 'Round floating point values for readability',
      sqlFragment: `ROUND(${quoteIdent(columnName)}, 2)`,
      column: columnName,
      type: 'round',
    });
  }

  // 5. Extract date from timestamp
  if (isTimestamp) {
    suggestions.push({
      label: 'Extract date only',
      description: 'Truncate timestamp to date',
      sqlFragment: `DATE_TRUNC('day', ${quoteIdent(columnName)})`,
      column: columnName,
      type: 'date_trunc',
    });
  }

  // 6. High duplication — suggest group by
  if (profile.unique_count > 0 && profile.count > 0) {
    const ratio = profile.unique_count / profile.count;
    if (ratio < 0.5 && profile.count > 10) {
      suggestions.push({
        label: `High duplication (${profile.unique_count} unique / ${profile.count.toLocaleString()})`,
        description: 'Group by this column to aggregate',
        sqlFragment: '', // handled via GROUP BY, not SELECT expression
        column: columnName,
        type: 'group_by',
      });
    }
  }

  return suggestions.slice(0, 3); // Show max 3 suggestions
}

// ── Utilities ────────────────────────────────────────────────────────

export type TransformChipKind =
  | 'filter'
  | 'groupBy'
  | 'sort'
  | 'column'
  | 'join'
  | 'union'
  | 'filled'
  | 'replaced'
  | 'lookup'
  | 'case'
  | 'renamed'
  | 'cast'
  | 'dropped'
  | 'window';

function getActiveLookupOutputColumns(state: TransformState): Set<string> {
  const cols = new Set<string>();
  (state.lookups || []).forEach((lookup, idx) => {
    for (const sourceCol of lookup.columns) {
      cols.add(lookup.columnAliases?.[sourceCol] ?? `L${idx + 1}_${sourceCol}`);
    }
  });
  return cols;
}

function isOrphanedLookupColumn(col: string, activeLookupColumns: Set<string>): boolean {
  return /^L\d+_/.test(col) && !activeLookupColumns.has(col);
}

function expressionReferencesOrphanedLookup(expr: string, activeLookupColumns: Set<string>): boolean {
  const quotedIdents = expr.match(/"([^"]+)"/g)?.map((s) => s.slice(1, -1)) ?? [];
  for (const ident of quotedIdents) {
    if (isOrphanedLookupColumn(ident, activeLookupColumns)) return true;
  }
  const bareTokens = expr.match(/\bL\d+_\w+\b/g) ?? [];
  for (const token of bareTokens) {
    if (isOrphanedLookupColumn(token, activeLookupColumns)) return true;
  }
  return false;
}

function unquoteIdentExpression(expr: string): string {
  const trimmed = expr.trim();
  if (trimmed.startsWith('"') && trimmed.endsWith('"')) {
    return trimmed.slice(1, -1).replace(/""/g, '"');
  }
  return trimmed;
}

function isCaseSelectColumn(col: SelectColumn): boolean {
  return /^CASE\s+WHEN\b/i.test(col.expression);
}

function isFilledSelectColumn(col: SelectColumn): boolean {
  return /^COALESCE\(/i.test(col.expression);
}

function isReplacedSelectColumn(col: SelectColumn): boolean {
  return !!col.isReplacement && (
    /^REPLACE\(/i.test(col.expression) ||
    /^REGEXP_REPLACE\(/i.test(col.expression)
  );
}

export function inferSelectColumnOutputType(
  col: Pick<SelectColumn, 'expression' | 'outputType'>,
): string | null {
  if (col.outputType) {
    return col.outputType.trim().toUpperCase();
  }
  const expression = col.expression;
  if (/^STRFTIME\s*\(/i.test(expression)) {
    const format = extractStrftimeOutputFormat(expression);
    return format ? inferStrftimeOutputType(format) : 'DATE';
  }
  if (/^TRY_CAST\(/i.test(expression)) {
    const match = expression.match(/\s+AS\s+([A-Z ]+)\)$/i);
    return match?.[1]?.trim().toUpperCase() || null;
  }
  const nestedCastMatch = expression.match(/TRY_CAST\(.+?\s+AS\s+([A-Z][A-Z0-9 ]*)\)/i);
  if (nestedCastMatch) {
    return nestedCastMatch[1].trim().toUpperCase();
  }
  if (/^try_strptime\(/i.test(expression)) {
    const match = expression.match(/::([A-Z]+)\s*$/i);
    return match?.[1]?.toUpperCase() || null;
  }
  if (
    /^CASE\s+WHEN\b/i.test(expression)
    && /\bTHEN\s+FALSE\b/i.test(expression)
    && /\bELSE\s+TRUE\s+END\s*$/i.test(expression)
  ) {
    return 'BOOLEAN';
  }
  return null;
}

function isCastSelectColumn(col: SelectColumn): boolean {
  return !!col.isReplacement && (
    !!col.outputType ||
    /^TRY_CAST\(/i.test(col.expression) ||
    /^try_strptime\(/i.test(col.expression)
  );
}

function isAddedSelectColumn(col: SelectColumn): boolean {
  return !!col.alias
    && col.expression !== '*'
    && !col.isReplacement
    && !col.isRename
    && col.expression !== quoteIdent(col.alias);
}

function shouldRemoveSelectColumnForKind(col: SelectColumn, kind: TransformChipKind): boolean {
  switch (kind) {
    case 'case':
      return isCaseSelectColumn(col);
    case 'filled':
      return isFilledSelectColumn(col);
    case 'replaced':
      return isReplacedSelectColumn(col);
    case 'cast':
      return isCastSelectColumn(col);
    case 'renamed':
      return !!col.isRename;
    case 'column':
      return isAddedSelectColumn(col);
    default:
      return false;
  }
}

function normalizeSelectColumnsAfterClear(selectColumns: SelectColumn[]): SelectColumn[] {
  if (
    selectColumns.length === 1
    && selectColumns[0].expression === '*'
    && !selectColumns[0].alias
  ) {
    return [];
  }
  return selectColumns;
}

function clearRenames(state: TransformState): TransformState {
  const renames = state.selectColumns.filter((col) => col.isRename);
  if (renames.length === 0) {
    return { ...state, selectColumns: state.selectColumns.filter((col) => !col.isRename) };
  }

  const revertName = (column: string): string => {
    for (const rename of renames) {
      if (rename.alias === column) {
        return unquoteIdentExpression(rename.expression);
      }
    }
    return column;
  };

  return {
    ...state,
    selectColumns: normalizeSelectColumnsAfterClear(
      state.selectColumns.filter((col) => !col.isRename),
    ),
    filters: state.filters.map((filter) => ({ ...filter, column: revertName(filter.column) })),
    orderBy: state.orderBy.map((sort) => ({ ...sort, column: revertName(sort.column) })),
    groupByColumns: state.groupByColumns.map(revertName),
    having: state.having.map((clause) => ({ ...clause, column: revertName(clause.column) })),
    windowFunctions: (state.windowFunctions ?? []).map((wf) => ({
      ...wf,
      outputCol: revertName(wf.outputCol),
      valueCol: wf.valueCol ? revertName(wf.valueCol) : wf.valueCol,
      partitionBy: wf.partitionBy.map(revertName),
      orderBy: wf.orderBy ? { ...wf.orderBy, col: revertName(wf.orderBy.col) } : wf.orderBy,
    })),
  };
}

/** Remove every transform in a toolbar chip category. */
export function clearTransformKind(state: TransformState, kind: TransformChipKind): TransformState {
  switch (kind) {
    case 'filter':
      return { ...state, filters: [] };
    case 'sort':
      return { ...state, orderBy: [] };
    case 'groupBy':
      return clearGroupBy(state);
    case 'join':
      return { ...state, joins: [] };
    case 'lookup':
      return { ...state, lookups: [] };
    case 'union':
      return {
        ...state,
        unions: [],
        unionDeduplicate: false,
        unionAlignMode: 'auto',
      };
    case 'dropped':
      return { ...state, excludeColumns: [] };
    case 'window':
      return { ...state, windowFunctions: [] };
    case 'renamed':
      return clearRenames(state);
    case 'case':
    case 'filled':
    case 'replaced':
    case 'cast':
    case 'column':
      return {
        ...state,
        selectColumns: normalizeSelectColumnsAfterClear(
          state.selectColumns.filter((col) => !shouldRemoveSelectColumnForKind(col, kind)),
        ),
      };
    default:
      return state;
  }
}

/** Transform categories that still reference lookup columns removed from state. */
export function getBrokenTransformKinds(state: TransformState): Set<TransformChipKind> {
  const activeLookupColumns = getActiveLookupOutputColumns(state);
  const broken = new Set<TransformChipKind>();

  const hasOrphan = (col: string) => isOrphanedLookupColumn(col, activeLookupColumns);
  const exprOrphan = (expr: string) => expressionReferencesOrphanedLookup(expr, activeLookupColumns);

  if (state.filters.some((f) => hasOrphan(f.column))) broken.add('filter');
  if (state.orderBy.some((o) => hasOrphan(o.column))) broken.add('sort');
  if (state.groupByColumns.some(hasOrphan)) broken.add('groupBy');
  if (state.having.some((h) => hasOrphan(h.column))) broken.add('groupBy');

  for (const wf of state.windowFunctions ?? []) {
    if (
      hasOrphan(wf.outputCol)
      || (wf.valueCol && hasOrphan(wf.valueCol))
      || wf.partitionBy.some(hasOrphan)
      || (wf.orderBy?.col && hasOrphan(wf.orderBy.col))
    ) {
      broken.add('window');
    }
  }

  for (const col of state.selectColumns) {
    if (!col.alias || col.expression === '*') continue;
    if (!hasOrphan(col.alias) && !exprOrphan(col.expression)) continue;

    if (col.isRename) {
      broken.add('renamed');
    } else if (/^CASE\s+WHEN\b/i.test(col.expression)) {
      broken.add('case');
    } else if (/^COALESCE\(/i.test(col.expression)) {
      broken.add('filled');
    } else if (
      col.isReplacement && (
        /^REPLACE\(/i.test(col.expression) ||
        /^REGEXP_REPLACE\(/i.test(col.expression)
      )
    ) {
      broken.add('replaced');
    } else if (
      col.isReplacement && (
        /^TRY_CAST\(/i.test(col.expression) ||
        /^try_strptime\(/i.test(col.expression)
      )
    ) {
      broken.add('cast');
    } else {
      broken.add('column');
    }
  }

  return broken;
}

/** Check if a TransformState has any modifications beyond the base SELECT * FROM table. */
export function isStateModified(state: TransformState): boolean {
  return (
    state.filters.length > 0 ||
    state.groupByColumns.length > 0 ||
    state.aggregations.length > 0 ||
    state.having.length > 0 ||
    state.orderBy.length > 0 ||
    state.joins.length > 0 ||
    (state.lookups || []).length > 0 ||
    state.unions.length > 0 ||
    (state.windowFunctions?.length ?? 0) > 0 ||
    state.selectColumns.length > 0 ||
    state.excludeColumns.length > 0 ||
    state.distinct
  );
}

/** Extract table references from SQL for input_refs. Best-effort regex. */
export function extractTableRefs(
  sql: string,
  workspaceTables: Array<{ name: string }>,
): string[] {
  const knownNames = new Set(workspaceTables.map((t) => t.name.toLowerCase()));
  const refs: string[] = [];
  // Match FROM/JOIN followed by quoted or unquoted identifier
  const regex = /(?:FROM|JOIN)\s+"?(\w+)"?/gi;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(sql)) !== null) {
    const name = match[1].toLowerCase();
    if (knownNames.has(name) && !refs.includes(name)) {
      refs.push(name);
    }
  }
  // UNION branches use FROM without JOIN — catch quoted table names in SELECT...FROM
  const fromRegex = /FROM\s+"([^"]+)"/gi;
  while ((match = fromRegex.exec(sql)) !== null) {
    const name = match[1].toLowerCase();
    if (knownNames.has(name) && !refs.includes(name)) {
      refs.push(name);
    }
  }
  return refs;
}

/** Generate a default output name that doesn't collide with existing tables. */
export function generateOutputName(existingTables: Array<{ name: string }>): string {
  const existing = new Set(existingTables.map((t) => t.name));
  let n = 1;
  while (existing.has(`transform_${n}`)) n++;
  return `transform_${n}`;
}

/**
 * Summarize what TransformState modifications were made (for diff display).
 * Returns a human-readable string like "Added 2 filters, grouped by country".
 */
export function summarizeChanges(state: TransformState): string {
  const parts: string[] = [];
  if (state.filters.length > 0) {
    parts.push(`${state.filters.length} filter${state.filters.length > 1 ? 's' : ''}`);
  }
  if (state.groupByColumns.length > 0) {
    parts.push(`grouped by ${state.groupByColumns.join(', ')}`);
  }
  if (state.orderBy.length > 0) {
    parts.push(`sorted by ${state.orderBy.map((o) => `${o.column} ${o.direction}`).join(', ')}`);
  }
  if (state.joins.length > 0) {
    parts.push(`${state.joins.length} join${state.joins.length > 1 ? 's' : ''}`);
  }
  if ((state.lookups || []).length > 0) {
    parts.push(`${(state.lookups || []).length} lookup${(state.lookups || []).length > 1 ? 's' : ''}`);
  }
  if (state.unions.length > 0) {
    const mode = state.unionDeduplicate ? 'union' : 'union all';
    parts.push(`${state.unions.length} ${mode} stack${state.unions.length > 1 ? 's' : ''}`);
  }
  if ((state.windowFunctions?.length ?? 0) > 0) {
    const count = state.windowFunctions?.length ?? 0;
    parts.push(`${count} window function${count > 1 ? 's' : ''}`);
  }
  if (state.selectColumns.length > 0) {
    // Don't count the wildcard * entry as a column transform
    const realCols = state.selectColumns.filter((c) => !(c.expression === '*' && !c.alias));
    // Column reorder materializes `"col" AS "col"` passthroughs — not new transforms
    const transformedCols = realCols.filter(
      (c) => !(c.alias && c.expression === quoteIdent(c.alias) && !c.isReplacement && !c.isRename),
    );
    const caseExpressions = transformedCols.filter((c) => /^CASE\s+WHEN\b/i.test(c.expression));
    const nullFills = transformedCols.filter((c) => /^COALESCE\(/.test(c.expression));
    const otherTransforms = transformedCols.length - caseExpressions.length - nullFills.length;

    if (caseExpressions.length > 0) {
      parts.push(`${caseExpressions.length} case expression${caseExpressions.length > 1 ? 's' : ''}`);
    }
    if (nullFills.length > 0) {
      parts.push(`${nullFills.length} missing-value fill${nullFills.length > 1 ? 's' : ''}`);
    }
    if (otherTransforms > 0) {
      parts.push(`${otherTransforms} column transform${otherTransforms > 1 ? 's' : ''}`);
    }
  }
  if (state.excludeColumns.length > 0) {
    parts.push(`${state.excludeColumns.length} column${state.excludeColumns.length > 1 ? 's' : ''} dropped`);
  }
  if (state.distinct) parts.push('distinct');
  return parts.join(', ') || 'No changes';
}
